/**
 * Plan Detail — read-only view of a single expedition plan.
 * Shows all populated fields, matching the form's section order.
 */

import { LitElement, html, svg, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { keyed } from 'lit/directives/keyed.js'
import { db } from '../../database/schema.js'
import { planStorage } from '../../storage/plan-storage.js'
import { backfillPlanAssociations } from '../../services/plan-association-service.js'
import { syncChildrenOnNavigate } from '../../storage/sync-manager.js'
import { featureStorage } from '../../storage/feature-storage.js'
import { featureCollection, feature as turfFeature, center } from '@turf/turf'
import { router } from '../../utils/router.js'
import type { Feature } from '../../schemas/feature.js'
import type { Plan, PlanLocation, PlanPackItem } from '../../schemas/plan.js'
import type { Route } from '../../schemas/route.js'
import type { Activity } from '../../schemas/activity.js'
import { groupPackItems } from '../../utils/pack-groups.js'
import { WeatherService } from '../../services/weather/weather-service.js'
import type {
  WeatherResult,
  HourlyForecast,
  DailyForecast,
  ElevationWeatherPoint,
} from '../../services/weather/weather-types.js'
import type { SkyCondition, LightningRisk } from '../../services/weather/weather-types.js'
import { capeToLightningRisk } from '../../services/weather/weather-utils.js'
import { elevationPointService } from '../../services/terrain/elevation-point-service.js'
import { avalancheLayerService } from '../../services/avalanche/avalanche-layer-service.js'
import type { AvalancheResult } from '../../services/avalanche/avalanche-types.js'
import type {
  AvalancheForecast,
  ForecastPeriod,
  AvalancheProblem,
  ElevationDanger,
  DangerLevel,
  Aspect,
  ElevationBand,
  AvalancheProblemType,
} from '../../services/avalanche/avalanche-types.js'
import { dangerLabel, dangerColor, problemLabel } from '../../services/avalanche/avalanche-utils.js'
import { SettingsStorage } from '../../storage/settings-storage.js'
import { convertDistance } from '../../utils/units.js'
import type { UnitSystem } from '../../utils/units.js'

import type { ChartPlanTime } from './weather-day-chart.js'
import './weather-day-chart.js'
import '@material/web/iconbutton/icon-button.js'
import '@material/web/progress/linear-progress.js'
import '@material/web/checkbox/checkbox.js'
import '@material/web/list/list.js'
import '@material/web/list/list-item.js'
import '@material/web/divider/divider.js'
import './trip-map-preview.js'
import '../common/confirm-dialog.js'
void import('../ui/rich-text-renderer.js')
import type { ConfirmDialog } from '../common/confirm-dialog.js'
import './route-promote-dialog.js'
import type { RoutePromoteDialog } from './route-promote-dialog.js'
import '../plans/contact-share-dialog.js'
import type { ContactShareDialog } from '../plans/contact-share-dialog.js'
import { authService } from '../../services/auth/auth-service.js'
import { edgeService, nodeRef } from '../../services/edge-service.js'
import { userStorage } from '../../storage/user-storage.js'
import type { Edge } from '../../schemas/edge.js'
import { showToast } from '../common/cairn-toast.js'
import { blockStore } from '../../services/moderation/block-store.js'
import { emergencyDataService } from '../../services/emergency/emergency-data-service.js'
import type {
  AnyEmergencyFacility,
  Hospital,
  LawEnforcement,
  FireStation,
} from '../../services/emergency/emergency-types.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/text-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import '../map/elevation-profile.js'
import '../map/track-aspect-rose.js'
import './field-log-tab.js'
import type { NamedProfile, ProfilePoint } from '../map/elevation-profile.js'
import type { AspectCounts } from '../map/track-aspect-rose.js'

type EmergencyFacilityWithDistance = AnyEmergencyFacility & {
  distanceKm: number
}

interface EmergencyServicesData {
  sheriffs: EmergencyFacilityWithDistance[]
  hospitals: EmergencyFacilityWithDistance[]
  traumaCenters: EmergencyFacilityWithDistance[]
  helipadHospitals: EmergencyFacilityWithDistance[]
  fireEMS: EmergencyFacilityWithDistance[]
  rangerStations: EmergencyFacilityWithDistance[]
}

@customElement('plan-detail')
export class PlanDetail extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      background: var(--cairn-background-color, #f8fafc);
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .container {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
    }

    .content-scroll {
      flex: 1;
      overflow: auto;
      transition: transform 0.15s ease-out;
    }

    .header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex-shrink: 0;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .back-button .material-icons {
      font-size: 24px;
      color: inherit;
      font-family: 'Material Symbols Outlined';
    }

    .header-title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .content-wrapper {
      flex: 1;
      padding: var(--cairn-spacing-lg, 24px);
      display: flex;
      justify-content: center;
    }

    .content {
      width: 100%;
      max-width: 800px;
    }

    .plan-header {
      display: flex;
      align-items: flex-start;
      gap: var(--cairn-spacing-sm, 8px);
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .plan-name {
      font-size: 28px;
      font-weight: 700;
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
      flex: 1;
    }

    .plan-actions {
      display: flex;
      gap: var(--cairn-spacing-xs, 4px);
      flex-shrink: 0;
    }

    .overview-section {
      overflow: hidden;
    }

    .overview-map {
      float: right;
      width: 280px;
      height: 250px;
      margin: 0 0 var(--cairn-spacing-md, 16px) var(--cairn-spacing-lg, 24px);
      border-radius: var(--cairn-border-radius, 8px);
      overflow: hidden;
    }

    .overview-map trip-map-preview {
      height: 100%;
      border-radius: var(--cairn-border-radius, 8px);
    }

    @media (max-width: 640px) {
      .overview-map {
        float: none;
        width: 100%;
        height: 200px;
        margin: 0 0 var(--cairn-spacing-md, 16px) 0;
      }
    }

    .route-charts {
      clear: both;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .route-chart-heading {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
      padding-bottom: var(--cairn-spacing-xs, 4px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .route-chart-row {
      display: flex;
      gap: var(--cairn-spacing-md, 16px);
      align-items: stretch;
    }

    .elevation-chart {
      flex: 1;
      height: 200px;
    }

    .aspect-chart {
      width: 180px;
      height: 200px;
      flex-shrink: 0;
    }

    @media (max-width: 640px) {
      .route-chart-row {
        flex-direction: column;
      }

      .elevation-chart {
        flex: none;
      }

      .aspect-chart {
        width: 100%;
        height: 180px;
      }
    }

    .section {
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .section-title {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
      padding-bottom: var(--cairn-spacing-xs, 4px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .related-row {
      clear: both;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin: var(--cairn-spacing-sm, 8px) 0 var(--cairn-spacing-md, 16px) 0;
    }

    .related-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: var(--cairn-text-size-sm, 14px);
      padding: 4px 10px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-color, #1e293b);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      font-family: inherit;
    }

    .related-chip:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      color: var(--cairn-primary-color, #3b82f6);
    }

    .related-chip .material-icons {
      font-size: 14px;
    }

    .meta {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .meta .material-icons {
      font-size: 16px;
      flex-shrink: 0;
    }

    .meta-separator {
      color: var(--cairn-border-color, #e2e8f0);
      margin: 0 2px;
      flex-shrink: 0;
    }

    .meta-date {
      min-width: 140px;
      flex-shrink: 0;
    }

    .meta-location {
      position: relative;
      overflow: hidden;
      white-space: nowrap;
      min-width: 0;
    }

    .meta-location::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 48px;
      background: linear-gradient(to right, transparent, var(--cairn-background-color, #f8fafc));
      pointer-events: none;
    }

    .text-block {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-color, #1e293b);
      line-height: 1.6;
      white-space: pre-wrap;
    }

    .subsection {
      margin-bottom: 12px;
    }

    .subsection-label {
      font-size: 13px;
      font-weight: 500;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: 4px;
    }

    /* Pack list */
    .team-gear,
    .individual-gear {
      margin-bottom: 16px;
    }

    .pack-item-qty {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      background: var(--cairn-surface-variant, #f1f5f9);
      padding: 2px 6px;
      border-radius: 4px;
      margin-left: 4px;
    }

    md-list {
      --md-list-container-color: transparent;
    }

    md-list-item {
      --md-list-item-top-space: 0px;
      --md-list-item-bottom-space: 0px;
      --md-list-item-one-line-container-height: 36px;
      --md-list-item-two-line-container-height: 48px;
    }

    md-list-item.packed {
      opacity: 0.45;
      text-decoration: line-through;
      --md-list-item-label-text-color: var(--cairn-text-secondary-color, #64748b);
    }

    .gear-group {
      padding-left: 12px;
    }

    .gear-group-label {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 0;
      margin-top: 4px;
      cursor: pointer;
      user-select: none;
    }

    .gear-group-label .material-icons {
      font-size: 16px;
      color: var(--cairn-text-secondary-color, #64748b);
      transition: transform 0.2s ease;
    }

    .gear-group-label .material-icons.collapsed {
      transform: rotate(-90deg);
    }

    .gear-group-name {
      font-size: 13px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
    }

    .gear-group-count {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-left: auto;
    }

    .gear-section-header {
      display: flex;
      align-items: center;
      cursor: pointer;
      user-select: none;
      gap: 4px;
      margin-bottom: 4px;
    }

    .gear-section-header .material-icons {
      font-size: 18px;
      color: var(--cairn-text-secondary-color, #64748b);
      transition: transform 0.2s ease;
    }

    .gear-section-header .material-icons.collapsed {
      transform: rotate(-90deg);
    }

    .gear-section-count {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-left: auto;
    }

    /* Timeline rail */
    .timeline {
      margin-top: var(--cairn-spacing-md, 16px);
      --tl-date-width: 56px;
      --tl-rail-left: calc(var(--tl-date-width) + 4px);
      --tl-content-left: calc(var(--tl-rail-left) + 36px);
      position: relative;
      padding-left: var(--tl-content-left);
    }

    /* Continuous vertical rail */
    .timeline::before {
      content: '';
      position: absolute;
      left: calc(var(--tl-rail-left) + 11px);
      top: 24px;
      bottom: 6px;
      width: 2px;
      background: var(--cairn-border-color, #e2e8f0);
    }

    /* Date separator line */
    .timeline-date-divider {
      position: relative;
      display: flow-root;
      margin-top: 12px;
      height: 1px;
      background: var(--cairn-border-color, #e2e8f0);
    }

    /* Extend the line left into the date label area */
    .timeline-date-divider::before {
      content: '';
      position: absolute;
      top: 0;
      right: 100%;
      width: var(--tl-content-left);
      height: 1px;
      background: var(--cairn-border-color, #e2e8f0);
    }

    .timeline-date-divider:first-child {
      margin-top: 0;
    }

    /* Date label inside first entry, left of the marker */
    .timeline-date-label {
      position: absolute;
      left: calc(-36px - 4px - var(--tl-date-width));
      top: 10px;
      width: var(--tl-date-width);
      font-size: 12px;
      font-weight: 600;
      color: var(--cairn-text-secondary-color, #64748b);
      text-transform: uppercase;
      text-align: right;
      letter-spacing: 0.02em;
      line-height: 1.3;
    }

    /* Entry row — clamp to header + 2 subtitle lines, expandable */
    .timeline-entry {
      position: relative;
      padding: 10px 0 0 0;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-color, #1e293b);
      /* header (~20px) + 2 subtitle lines (2 * 13px * 1.4 + margins) */
      cursor: pointer;
    }

    /* Fade at bottom when clamped and overflowing */
    .timeline-entry.overflowing .timeline-entry-content::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 16px;
      background: linear-gradient(to bottom, transparent, var(--cairn-background-color, #f8fafc));
      pointer-events: none;
    }

    .timeline-entry.expanded .timeline-entry-content::after {
      display: none;
    }

    /* Node dot on the rail */
    .timeline-node {
      position: absolute;
      left: calc(-36px);
      top: 10px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--cairn-surface-color, #ffffff);
      border: 2px solid var(--cairn-border-color, #e2e8f0);
      z-index: 1;
    }

    .timeline-node .material-icons {
      font-size: 14px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .timeline-node.meeting {
      border-color: var(--cairn-primary-color, #3b82f6);
    }
    .timeline-node.meeting .material-icons {
      color: var(--cairn-primary-color, #3b82f6);
    }

    .timeline-node.start {
      border-color: #22c55e;
    }
    .timeline-node.start .material-icons {
      color: #22c55e;
    }

    .timeline-node.turnaround {
      border-color: #f59e0b;
    }
    .timeline-node.turnaround .material-icons {
      color: #f59e0b;
    }

    .timeline-node.return {
      border-color: var(--cairn-primary-color, #3b82f6);
    }
    .timeline-node.return .material-icons {
      color: var(--cairn-primary-color, #3b82f6);
    }

    .timeline-node.overdue {
      border-color: #ef4444;
    }
    .timeline-node.overdue .material-icons {
      color: #ef4444;
    }

    .timeline-entry-content {
      position: relative;
      /* header + location line + description line + bottom padding */
      min-height: 64px;
      max-height: 64px;
      overflow: hidden;
      transition: max-height 0.25s ease;
    }

    .timeline-entry.expanded .timeline-entry-content {
      max-height: 500px;
    }

    .timeline-entry-header {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }

    .time-name {
      font-weight: 500;
    }

    .time-value {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 13px;
    }

    .time-subtitle {
      position: relative;
      margin-top: 2px;
      font-size: 13px;
      line-height: 1.4;
      white-space: nowrap;
      overflow: hidden;
      max-height: 1.4em;
    }

    /* Right-fade on truncated location/note lines */
    .time-subtitle.text-overflow::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 48px;
      background: linear-gradient(to right, transparent, var(--cairn-background-color, #f8fafc));
      pointer-events: none;
    }

    .timeline-entry.expanded .time-subtitle {
      white-space: normal;
      overflow: visible;
      max-height: none;
    }

    .timeline-entry.expanded .time-subtitle.text-overflow::after {
      display: none;
    }

    .time-subtitle:empty {
      cursor: default;
    }

    .time-subtitle:empty::after {
      display: none;
    }

    .time-note-text {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    /* Decision table (read-only) */
    .decision-table-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
    }

    .decision-table {
      width: 100%;
      min-width: 500px;
      border-collapse: collapse;
      table-layout: fixed;
    }

    .decision-table th,
    .decision-table td {
      padding: 8px 12px;
      text-align: left;
      vertical-align: top;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      font-size: 14px;
    }

    .decision-table th {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: white;
    }

    .decision-table th.col-factor {
      width: 130px;
      background: var(--cairn-surface-color, #fff);
      color: var(--cairn-text-color, #1e293b);
    }
    .decision-table th.col-green {
      background: #166534;
    }
    .decision-table th.col-yellow {
      background: #92400e;
    }
    .decision-table th.col-red {
      background: #991b1b;
    }

    .decision-table td.cell-green {
      background: #f0fdf4;
      color: #14532d;
    }
    .decision-table td.cell-yellow {
      background: #fffbeb;
      color: #78350f;
    }
    .decision-table td.cell-red {
      background: #fef2f2;
      color: #7f1d1d;
    }

    .decision-table tr:last-child td {
      border-bottom: none;
    }

    .decision-table tr.rules-row td {
      border-top: 2px solid var(--cairn-border-color, #e2e8f0);
      font-weight: 600;
    }

    .maps-link {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      color: var(--cairn-primary-color, #3b82f6);
      text-decoration: none;
    }

    .maps-link:hover {
      text-decoration: underline;
    }

    .maps-link .material-icons {
      font-size: 14px;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      display: inline-block;
    }

    /* Tab bar */
    .tab-bar {
      display: flex;
      align-items: center;
      gap: 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .tab {
      padding: 10px 16px;
      font-size: var(--cairn-text-size-sm, 14px);
      font-weight: 500;
      color: var(--cairn-text-secondary-color, #64748b);
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      cursor: pointer;
      transition:
        color 0.15s,
        border-color 0.15s;
      margin-bottom: -1px;
    }

    .tab:hover {
      color: var(--cairn-text-color, #1e293b);
    }

    .tab.active {
      color: var(--cairn-primary-color, #3b82f6);
      border-bottom-color: var(--cairn-primary-color, #3b82f6);
    }

    .tab-content {
      display: none;
    }

    .tab-content.active {
      display: block;
    }

    .empty-tab {
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px) 0;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    /* Weather conditions */
    .weather-current {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-lg, 24px);
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border-radius: var(--cairn-border-radius, 8px);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      margin-bottom: var(--cairn-spacing-md, 16px);
      position: relative;
    }

    .weather-current-time {
      position: absolute;
      top: var(--cairn-spacing-sm, 8px);
      right: var(--cairn-spacing-sm, 8px);
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-current-main {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex-shrink: 0;
    }

    .weather-current-icon {
      font-size: 40px;
      color: var(--cairn-primary-color, #3b82f6);
      line-height: 1;
    }

    .weather-current-temp {
      font-size: 36px;
      font-weight: 700;
      color: var(--cairn-text-color, #1e293b);
      line-height: 1;
    }

    .weather-current-feels {
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-top: 2px;
    }

    .weather-current-condition {
      font-size: var(--cairn-text-size-sm, 14px);
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
    }

    .weather-current-details {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-lg, 24px);
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-detail {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .weather-detail .material-icons {
      font-size: 16px;
    }

    /* Hourly forecast strip */
    .weather-hourly-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 6px;
    }

    .weather-hourly-label {
      font-size: 13px;
      font-weight: 500;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-updated {
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 12px;
    }

    .weather-chip {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 16px;
      background: none;
      font-size: 12px;
      cursor: pointer;
      color: var(--cairn-text-color, #1e293b);
    }

    .weather-chip.active {
      background: var(--cairn-primary-color, #3b82f6);
      border-color: var(--cairn-primary-color, #3b82f6);
      color: white;
    }

    .weather-chip-elev {
      font-size: 10px;
      opacity: 0.7;
    }

    .weather-hourly-controls {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .weather-granularity-toggle {
      display: flex;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 4px;
      overflow: hidden;
    }

    .granularity-btn {
      background: none;
      border: none;
      padding: 2px 8px;
      font-size: 11px;
      cursor: pointer;
      color: var(--cairn-text-secondary-color, #64748b);
      border-right: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .granularity-btn:last-child {
      border-right: none;
    }

    .granularity-btn.active {
      background: var(--cairn-primary-color, #3b82f6);
      color: white;
    }

    .weather-hourly {
      display: flex;
      gap: 2px;
      overflow-x: auto;
      padding-bottom: 4px;
      margin-bottom: var(--cairn-spacing-md, 16px);
      scrollbar-width: thin;
    }

    .weather-hour {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      min-width: 56px;
      padding: 8px 6px;
      border-radius: 8px;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      font-size: 12px;
      color: var(--cairn-text-color, #1e293b);
    }

    .weather-hour-time {
      font-weight: 500;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 11px;
    }

    .weather-hour-icon {
      font-size: 20px;
      color: var(--cairn-primary-color, #3b82f6);
      line-height: 1;
    }

    .weather-hour-temp {
      font-weight: 600;
    }

    .weather-hour-precip-slot {
      display: flex;
      align-items: center;
      gap: 2px;
    }

    .weather-hour-precip-icon {
      font-size: 14px;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .weather-hour-accum {
      font-size: 10px;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .weather-hour-lightning {
      font-size: 16px;
    }

    /* Daily forecast */
    .weather-daily {
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .weather-day {
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-color, #1e293b);
    }

    .weather-day:last-child {
      border-bottom: none;
    }

    .weather-day.plan-day {
      background: rgba(59, 130, 246, 0.08);
      font-weight: 600;
    }

    .weather-day.plan-day .weather-day-low {
      font-weight: 600;
    }

    .weather-day-summary {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      padding: 8px 0;
      cursor: pointer;
    }

    .weather-day-summary:hover {
      background: rgba(0, 0, 0, 0.03);
    }

    .weather-day-chevron {
      font-size: 18px;
      color: var(--cairn-text-secondary-color, #64748b);
      flex-shrink: 0;
    }

    .weather-day-detail {
      padding: 0 0 12px 26px;
    }

    .weather-day-detail-meta {
      display: flex;
      gap: 12px;
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: 8px;
    }

    .weather-day-detail-empty {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
    }

    .weather-day-blocks {
      display: flex;
      gap: 2px;
      overflow-x: auto;
    }

    .weather-block {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
      min-width: 56px;
      padding: 6px 4px;
      border-radius: 6px;
      background: rgba(0, 0, 0, 0.02);
      font-size: 12px;
    }

    .weather-block-time {
      font-weight: 600;
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-block-icon {
      font-size: 18px;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .weather-block-temp {
      font-weight: 600;
    }

    .weather-block-feels {
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-block-wind {
      display: flex;
      align-items: center;
      gap: 1px;
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-block-wind .material-icons {
      font-size: 12px;
    }

    .weather-block-precip {
      display: flex;
      align-items: center;
      gap: 1px;
      font-size: 11px;
      color: var(--cairn-primary-color, #3b82f6);
    }

    .weather-block-precip .material-icons {
      font-size: 12px;
    }

    .weather-block-no-precip {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-block-lightning {
      font-size: 14px;
    }

    .weather-block-spread {
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #64748b);
      font-style: italic;
    }

    .weather-block-wind-spread {
      font-size: 9px;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-left: 2px;
    }

    .weather-day-source {
      margin-bottom: 8px;
    }

    .weather-day-source-label {
      font-size: 11px;
      font-weight: 600;
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: 4px;
      display: block;
    }

    .weather-day-detail-hint {
      font-style: italic;
      font-size: 10px;
    }

    .weather-hour-elev {
      display: flex;
      gap: 3px;
      font-size: 9px;
      color: var(--cairn-text-secondary-color, #64748b);
      border-top: 1px dashed rgba(0, 0, 0, 0.1);
      padding-top: 2px;
      margin-top: 2px;
    }

    .weather-hour-elev-temp {
      color: #2563eb;
    }

    .weather-hour-elev-wind {
      color: #64748b;
    }

    .weather-day-elev-note {
      display: flex;
      gap: 6px;
      align-items: center;
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #64748b);
      order: 5;
    }

    .weather-day-row1 {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      flex: 1;
      min-width: 0;
      order: 1;
    }

    .weather-day-name {
      width: 100px;
      font-weight: 500;
      flex-shrink: 0;
    }

    .weather-day-icon {
      font-size: 20px;
      color: var(--cairn-primary-color, #3b82f6);
      line-height: 1;
      flex-shrink: 0;
    }

    .weather-day-condition {
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-day-precip {
      display: flex;
      align-items: center;
      gap: 2px;
      font-size: 13px;
      color: var(--cairn-primary-color, #3b82f6);
      flex-shrink: 0;
    }

    .weather-day-precip .material-icons {
      font-size: 14px;
    }

    .weather-day-row2 {
      display: flex;
      align-items: center;
      gap: 2px;
      flex-shrink: 0;
      font-size: 13px;
      order: 2;
    }

    .weather-day-wind {
      display: flex;
      align-items: center;
      gap: 2px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .weather-day-wind .material-icons {
      font-size: 14px;
    }

    .weather-day-lightning {
      font-size: 16px;
      margin-right: 2px;
    }

    .weather-day-temps {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
      order: 3;
    }

    .weather-day-high {
      font-weight: 600;
    }

    .weather-day-low {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    @media (max-width: 640px) {
      .weather-day-row2 {
        order: 4;
        flex-basis: 100%;
      }
      .weather-day-wind {
        margin-left: auto;
      }
      .weather-day-temps {
        margin-left: auto;
      }
    }

    .weather-show-more {
      display: block;
      margin: 8px auto 0;
      padding: 4px 12px;
      background: none;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 4px;
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .weather-show-more:hover {
      background: rgba(0, 0, 0, 0.03);
    }

    .weather-no-location {
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px) 0;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .weather-no-location .material-icons {
      font-size: 32px;
      display: block;
      margin: 0 auto 8px;
    }

    .weather-loading {
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px) 0;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .tab-refresh {
      margin-left: auto;
      --md-icon-button-icon-size: 20px;
    }

    .tab-refresh.spinning span {
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    /* Avalanche forecast styles */
    .avy-title-row {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-sm, 8px);
      margin-bottom: var(--cairn-spacing-sm, 8px);
      padding-bottom: var(--cairn-spacing-xs, 4px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .avy-title-row .section-title {
      margin: 0;
      padding-bottom: 0;
      border-bottom: none;
    }

    .avy-source-tag {
      display: inline-block;
      font-size: var(--cairn-text-size-xs, 12px);
      font-weight: 600;
      padding: 2px 8px;
      border-radius: 999px;
      background: var(--cairn-primary-color, #1976d2);
      color: #fff;
      text-decoration: none;
      white-space: nowrap;
    }

    .avy-banner {
      padding: var(--cairn-spacing-sm, 8px) var(--cairn-spacing-md, 12px);
      border-radius: var(--cairn-radius-sm, 6px);
      font-size: var(--cairn-text-size-sm, 14px);
      margin-bottom: var(--cairn-spacing-md, 12px);
    }

    .avy-banner-info {
      background: #e0f2fe;
      color: #0c4a6e;
    }

    .avy-banner-warning {
      background: #fef3c7;
      color: #92400e;
    }

    .avy-period {
      margin-bottom: var(--cairn-spacing-lg, 16px);
      padding-bottom: var(--cairn-spacing-md, 12px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .avy-period:last-child {
      border-bottom: none;
    }

    .avy-period-date {
      font-weight: 600;
      font-size: var(--cairn-text-size-sm, 14px);
      margin-bottom: var(--cairn-spacing-sm, 8px);
      color: var(--cairn-text-primary-color, #1e293b);
    }

    /* Period header: danger triangle + bottom line side by side */
    .avy-period-header {
      display: flex;
      gap: var(--cairn-spacing-md, 12px);
      align-items: flex-start;
      margin-bottom: var(--cairn-spacing-md, 12px);
    }

    .avy-period-header .avy-danger-triangle-wrap {
      flex-shrink: 0;
    }

    .avy-bottom-line {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #94a3b8);
      line-height: 1.5;
      flex: 1;
      min-width: 0;
    }

    /* Problems list: horizontal scroll on mobile */
    .avy-problems {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
    }

    /* Each problem row: horizontally scrollable */
    .avy-problem-row {
      display: flex;
      align-items: flex-start;
      gap: 4px;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .avy-problem-col {
      display: flex;
      flex-direction: column;
      align-items: center;
      flex-shrink: 0;
    }

    .avy-problem-col-title {
      font-weight: 600;
      font-size: 10px;
      color: var(--cairn-text-secondary-color, #94a3b8);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
      text-align: center;
      max-width: 108px;
    }

    .avy-problem-icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .avy-problem-icon img {
      height: 100px;
      width: 100px;
      object-fit: contain;
      background: #fff;
      border-radius: 6px;
      padding: 4px;
    }

    .avy-problem-col .avy-rose-svg {
      width: 136px;
      height: 136px;
    }

    .avy-problem-col .avy-scale-svg {
      height: 136px;
    }

    /* Carousel navigation */
    .avy-carousel-nav {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--cairn-spacing-sm, 8px);
      margin-bottom: var(--cairn-spacing-md, 12px);
    }

    .avy-carousel-date {
      font-weight: 600;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-primary-color, #e2e8f0);
      min-width: 120px;
      text-align: center;
    }

    .avy-carousel-btn {
      background: none;
      border: none;
      color: var(--cairn-text-primary-color, #e2e8f0);
      padding: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .avy-carousel-btn:disabled {
      opacity: 0.3;
      cursor: default;
    }

    .avy-danger-triangle-wrap {
      position: relative;
      width: 160px;
      height: 140px;
    }

    .avy-danger-triangle {
      display: block;
    }

    .avy-period-centered {
      display: flex;
      justify-content: center;
      margin-bottom: var(--cairn-spacing-md, 12px);
    }

    .avy-no-data-wrap {
      width: 160px;
      height: 140px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* NAPADS icons positioned over each triangle tier */
    .avy-tri-icon {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .avy-tri-icon-alp {
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
    }
    .avy-tri-icon-tln {
      top: 54px;
      left: 50%;
      transform: translateX(-50%);
    }
    .avy-tri-icon-btl {
      top: 96px;
      left: 50%;
      transform: translateX(-50%);
    }

    .avy-tri-tooltip {
      display: none;
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      margin-left: 8px;
      white-space: nowrap;
      background: var(--cairn-surface-color, #1e293b);
      color: var(--cairn-text-primary-color, #e2e8f0);
      border: 1px solid var(--cairn-border-color, #334155);
      border-radius: var(--cairn-radius-sm, 6px);
      padding: 4px 8px;
      font-size: var(--cairn-text-size-xs, 12px);
      font-weight: 600;
      pointer-events: none;
      z-index: 10;
    }

    .avy-tri-icon:hover .avy-tri-tooltip,
    .avy-tri-icon:focus .avy-tri-tooltip {
      display: block;
    }

    .avy-tri-icon:focus {
      outline: none;
    }

    .avy-danger-badge {
      display: inline-block;
      padding: 2px 10px;
      border-radius: var(--cairn-radius-sm, 6px);
      font-size: var(--cairn-text-size-xs, 12px);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .avy-danger-overall {
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    .avy-rose-svg,
    .avy-scale-svg {
      color: var(--cairn-text-primary-color, #e2e8f0);
    }

    .avy-problem-detail {
      font-size: var(--cairn-text-size-xs, 12px);
      color: var(--cairn-text-secondary-color, #94a3b8);
    }

    .avy-detail-label {
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
    }

    .avy-travel-advice {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-color, #1e293b);
      line-height: 1.5;
      margin-bottom: var(--cairn-spacing-sm, 8px);
    }

    @media (max-width: 640px) {
      .weather-current {
        flex-direction: column;
        align-items: flex-start;
        gap: var(--cairn-spacing-sm, 8px);
      }
    }

    @media (min-width: 769px) {
      .header {
        display: none;
      }
    }

    /* ── Plan member invite form & list ── */

    .members-section {
      margin-top: 12px;
    }

    .members-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .members-heading {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .members-heading .material-icons {
      font-size: 18px;
    }

    .invite-form {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: 12px;
      margin-bottom: 8px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .invite-form-actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 8px;
    }

    .invite-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .invite-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px 4px 12px;
      border-radius: 16px;
      font-size: var(--cairn-text-size-sm, 14px);
      background: var(--cairn-primary-bg-light-color, rgba(59, 130, 246, 0.15));
      color: var(--cairn-text-color, #1e293b);
    }

    .invite-chip button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border: none;
      background: none;
      border-radius: 50%;
      cursor: pointer;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 16px;
      font-family: 'Material Symbols Outlined';
    }

    .invite-chip button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-color, #1e293b);
    }

    .suggestions {
      margin-top: 4px;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      max-height: 200px;
      overflow-y: auto;
    }

    .suggestion-item {
      display: flex;
      flex-direction: column;
      padding: 10px 12px;
      cursor: pointer;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .suggestion-item:last-child {
      border-bottom: none;
    }

    .suggestion-item:hover {
      background: var(--cairn-primary-bg-light-color, rgba(59, 130, 246, 0.04));
    }

    .suggestion-name {
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .suggestion-detail {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-xs, 12px);
    }

    .suggestion-icon {
      font-size: 14px;
      margin-right: 4px;
      vertical-align: middle;
      font-family: 'Material Symbols Outlined';
    }

    .member-item {
      display: flex;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid var(--cairn-border-color, rgba(255, 255, 255, 0.1));
      cursor: pointer;
    }

    .member-item:last-child {
      border-bottom: none;
    }

    .member-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
      margin-right: 12px;
    }

    .member-avatar-placeholder {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 14px;
      color: white;
      background: #94a3b8;
      flex-shrink: 0;
      margin-right: 12px;
    }

    .member-info {
      flex: 1;
      min-width: 0;
    }

    .member-name {
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
    }

    .member-you {
      opacity: 0.5;
      font-weight: 400;
    }

    .role-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: var(--cairn-text-size-xs, 12px);
      font-weight: 500;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    .role-badge.leader {
      background: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }

    .role-badge.member {
      background: rgba(100, 116, 139, 0.1);
      color: #64748b;
    }

    .role-badge.viewer {
      background: rgba(34, 197, 94, 0.1);
      color: #16a34a;
    }

    .member-expanded {
      padding: 8px 0 8px 48px;
      border-bottom: 1px solid var(--cairn-border-color, rgba(255, 255, 255, 0.1));
    }

    .member-contact-row {
      font-size: 13px;
      opacity: 0.7;
      margin-top: 4px;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .member-contact-row .material-icons {
      font-size: 14px;
      font-family: 'Material Symbols Outlined';
    }

    .member-actions {
      display: flex;
      gap: 4px;
      margin-top: 8px;
      padding-left: 48px;
    }

    .emergency-facility-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      border-bottom: 1px solid var(--cairn-border-color-light, #f1f5f9);
    }

    .emergency-facility-row:last-child {
      border-bottom: none;
    }

    .emergency-facility-details {
      flex: 1;
      min-width: 0;
    }

    .emergency-facility-name {
      font-size: 14px;
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .emergency-facility-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 2px 8px;
      font-size: 12px;
      color: var(--cairn-text-secondary, #64748b);
      margin-top: 2px;
    }

    .emergency-dist {
      font-weight: 500;
    }

    .emergency-type-label {
      opacity: 0.8;
    }

    .emergency-phone-inline {
      color: var(--cairn-primary-color, #3f5a7a);
      text-decoration: none;
    }

    .emergency-address-link {
      display: block;
      font-size: 11px;
      color: var(--cairn-primary-color, #3f5a7a);
      text-decoration: none;
      margin-top: 2px;
    }

    .emergency-address-link:hover {
      text-decoration: underline;
    }

    .emergency-phone {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: var(--cairn-surface-color, #f1f5f9);
      color: var(--cairn-primary-color, #3f5a7a);
      text-decoration: none;
      flex-shrink: 0;
    }

    .emergency-phone .material-icons {
      font-size: 18px;
    }

    .emergency-expand {
      display: flex;
      align-items: center;
      gap: 4px;
      background: none;
      border: none;
      color: var(--cairn-primary-color, #3f5a7a);
      font-size: 13px;
      cursor: pointer;
      padding: 6px 0;
      margin-top: 4px;
    }

    .emergency-expand .material-icons {
      font-size: 18px;
    }
  `

  @property({ type: String }) planId?: string
  @state() private plan: Plan | null = null
  @state() private syncing = false
  @state() private lastSyncTime = 0
  @state() private routeFeatures: Feature[] = []
  @state() private linkedRoute: Route | null = null
  @state() private linkedActivities: Activity[] = []
  @state() private collapsedSections = new Set<string>()
  @property({ type: String }) tab: 'overview' | 'pack-list' | 'conditions' | 'field-log' =
    'overview'
  @state() private weather: WeatherResult | null = null
  @state() private weatherLoading = false
  @state() private avalanche: AvalancheResult | null = null
  @state() private avalancheLoading = false
  @state() private avyPeriodIndex = 0
  @state() private avyDayLoading = false
  private avyDateRange: string[] = []
  private avyFetchedDates = new Set<string>()
  @state() private elevationWeatherPoints: ElevationWeatherPoint[] = []
  @state() private activeWeatherChips = new Set<string>(['area'])
  @state() private members: Array<{
    userId: string
    displayName: string
    picture?: string
    role: string
    sharedContact?: {
      phone?: string
      email?: string
      emergencyContact?: { name: string; phone: string; relationship: string }
    }
  }> = []

  @state() private routeChartData: Array<{
    name: string
    profile: NamedProfile
    aspectCounts: AspectCounts
  }> = []

  private chartGeneration = 0
  @state() private mapVersion = 0

  @state() private expandedMemberId: string | null = null
  @state() private emergencyServices: EmergencyServicesData | null = null
  @state() private emergencyLoading = false
  @state() private emergencyExpanded = false
  @state() private expandedWeatherDays = new Set<string>()
  @state() private hourlyGranularity: 1 | 3 | 6 = 1
  @state() private weatherExtraPadding = 0

  private unsubscribeRouter?: () => void
  private weatherService = new WeatherService()
  private avalancheService = avalancheLayerService
  private settingsStorage = new SettingsStorage()

  /** Get current unit system from user settings */
  private get units(): UnitSystem {
    return this.settingsStorage.getSettingsWithDefaults().unitSystem
  }

  /** Convert temperature from °F (API) to display value */
  private tempVal(f: number): number {
    return this.units === 'metric' ? Math.round(((f - 32) * 5) / 9) : Math.round(f)
  }

  /** Temperature unit label */
  private get tempUnit(): string {
    return this.units === 'metric' ? '°C' : '°F'
  }

  /** Convert wind speed from mph (API) to display value */
  private windVal(mph: number): number {
    return this.units === 'metric' ? Math.round(mph * 1.60934) : Math.round(mph)
  }

  /** Wind speed unit label */
  private get windUnit(): string {
    return this.units === 'metric' ? 'km/h' : 'mph'
  }

  /** Convert precipitation from inches (API) to display value */
  private precipVal(inches: number): string {
    if (this.units === 'metric') {
      return (inches * 25.4).toFixed(0)
    }
    return inches.toFixed(1)
  }

  /** Precipitation unit label */
  private get precipUnit(): string {
    return this.units === 'metric' ? 'mm' : '″'
  }

  override connectedCallback(): void {
    super.connectedCallback()
    if (this.planId) {
      void this.loadPlan()
      this.syncing = true
      void syncChildrenOnNavigate('plan', this.planId)
        .then(async () => {
          await this.loadPlan()
          this.mapVersion++
        })
        .finally(() => {
          this.syncing = false
        })
    }
    // Sync tab to current URL, and subscribe to future route changes
    const tabFromPath = (): 'overview' | 'pack-list' | 'conditions' | 'field-log' => {
      const path = window.location.pathname
      if (path.endsWith('/pack-list')) return 'pack-list'
      if (path.endsWith('/conditions')) return 'conditions'
      if (path.endsWith('/field-log')) return 'field-log'
      return 'overview'
    }
    const urlTab = tabFromPath()
    if (urlTab !== 'overview') this.tab = urlTab
    this.unsubscribeRouter = router.subscribe(() => {
      const newTab = tabFromPath()
      if (newTab !== this.tab) {
        this.tab = newTab
      }
    })
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubscribeRouter?.()
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('planId') && this.planId) void this.loadPlan()
    if (changed.has('tab') && this.tab === 'conditions') {
      void this.loadWeather()
      void this.loadAvalanche()
    }
    if (changed.has('plan') && this.tab === 'conditions') {
      void this.loadWeather()
      void this.loadAvalanche()
    }
    this.detectOverflow()
  }

  private async loadWeather(forceRefresh = false): Promise<void> {
    if (!this.plan) return
    const coords = this.resolvePlanCoordinates()
    if (!coords) return

    this.weatherLoading = true
    const dateRange = this.resolvePlanDateRange()
    let expandedRange = dateRange ?? undefined
    if (expandedRange && this.weatherExtraPadding > 0) {
      expandedRange = {
        startDate: PlanDetail.shiftDate(expandedRange.startDate, -this.weatherExtraPadding),
        endDate: PlanDetail.shiftDate(expandedRange.endDate, this.weatherExtraPadding),
      }
    }
    const result = await this.weatherService.getWeather(coords[0], coords[1], {
      dateRange: expandedRange,
      forceRefresh,
      includeEnsemble: true,
    })
    this.weather = result
    this.weatherLoading = false

    // DEBUG: weather data overview
    if (result) {
      const ens = result.ensemble
      console.group('[weather] data loaded')
      console.log('hourly count:', result.data.hourly.length)
      console.log(
        'daily dates:',
        result.data.daily.map(d => d.date)
      )
      console.log(
        'cape values (first 6):',
        result.data.hourly.slice(0, 6).map(h => ({ time: h.time, cape: h.cape }))
      )
      console.log(
        'ensemble:',
        ens
          ? {
              spreadCount: ens.spreads.length,
              spreadDates: [...new Set(ens.spreads.map(s => s.time.slice(0, 10)))],
              sampleSpread: ens.spreads[0],
            }
          : 'null'
      )
      console.groupEnd()
    }

    // Load elevation-zone weather for point features
    void this.loadElevationWeather()
  }

  private async loadElevationWeather(): Promise<void> {
    const pointFeatures = this.routeFeatures.filter(
      f => f.type === 'point' && f.geometry.type === 'Point'
    )
    if (pointFeatures.length === 0) {
      this.elevationWeatherPoints = []
      return
    }

    const elevService = elevationPointService
    const dateRange = this.resolvePlanDateRange()
    let expandedRange = dateRange ?? undefined
    if (expandedRange && this.weatherExtraPadding > 0) {
      expandedRange = {
        startDate: PlanDetail.shiftDate(expandedRange.startDate, -this.weatherExtraPadding),
        endDate: PlanDetail.shiftDate(expandedRange.endDate, this.weatherExtraPadding),
      }
    }

    const points: ElevationWeatherPoint[] = await Promise.all(
      pointFeatures.map(async f => {
        const coords = (f.geometry as GeoJSON.Point).coordinates
        const lon = coords[0]
        const lat = coords[1]
        const elevM = await elevService.getElevation(lat, lon)
        const elevFt = elevM != null ? Math.round(elevM * 3.28084) : 0

        const result = await this.weatherService.getWeather(lat, lon, {
          dateRange: expandedRange,
          exactCoords: true,
        })
        return {
          id: f.id,
          label: f.name,
          lat,
          lon,
          elevationFt: elevFt,
          data: result?.data ?? null,
        }
      })
    )

    this.elevationWeatherPoints = points.sort((a, b) => a.elevationFt - b.elevationFt)
    this.activeWeatherChips = new Set(['area'])
  }

  private async loadAvalanche(forceRefresh = false): Promise<void> {
    if (!this.plan) return
    const coords = this.resolvePlanCoordinates()
    if (!coords) return

    this.avalancheLoading = true
    try {
      const dateRange = this.resolvePlanDateRange()
      const today = new Date().toISOString().slice(0, 10)

      // Build the date range for carousel navigation (±1 day around trip)
      const rawStart = dateRange?.startDate ?? today
      const rawEnd = dateRange?.endDate ?? today
      const start = PlanDetail.shiftDate(rawStart, -1)
      const end = PlanDetail.shiftDate(rawEnd, 1)

      this.avyDateRange = []
      const d = new Date(start + 'T12:00:00Z')
      const endD = new Date(end + 'T12:00:00Z')
      while (d <= endD) {
        this.avyDateRange.push(d.toISOString().slice(0, 10))
        d.setUTCDate(d.getUTCDate() + 1)
      }

      // Determine initial day to show
      let initialDate = today
      if (today >= rawStart && today <= rawEnd) {
        initialDate = today
      } else if (today < rawStart) {
        initialDate = start
      } else {
        initialDate = rawEnd
      }

      // Fetch just the initial day
      this.avyFetchedDates.clear()
      const result = await this.avalancheService.getForecast(
        coords[0],
        coords[1],
        initialDate,
        initialDate,
        forceRefresh
      )

      // Fall back to today if no data for initial date
      if (!result?.data?.periods.length && initialDate !== today) {
        const fallback = await this.avalancheService.getForecast(
          coords[0],
          coords[1],
          today,
          today,
          forceRefresh
        )
        if (fallback?.data?.periods.length) {
          this.avalanche = fallback
          this.avyFetchedDates.add(today)
        } else {
          this.avalanche = result
        }
      } else {
        this.avalanche = result
        this.avyFetchedDates.add(initialDate)
      }

      // Set carousel to initial date
      if (this.avalanche?.data?.periods.length) {
        this.avyPeriodIndex = this.avyDateRange.indexOf(initialDate)
        if (this.avyPeriodIndex < 0) this.avyPeriodIndex = 0
      }

      // Prefetch adjacent day
      void this.prefetchAvyDay(initialDate)
    } catch (err) {
      console.error('[plan-detail] Failed to load avalanche forecast:', err)
    } finally {
      this.avalancheLoading = false
    }
  }

  private async loadAvyDay(date: string): Promise<void> {
    if (this.avyFetchedDates.has(date)) return
    const coords = this.resolvePlanCoordinates()
    if (!coords) return

    this.avyDayLoading = true
    try {
      const result = await this.avalancheService.getForecast(coords[0], coords[1], date, date)

      if (result?.data?.periods.length) {
        this.avyFetchedDates.add(date)
        // Merge new periods into existing avalanche data
        if (this.avalanche?.data) {
          const existing = new Map(this.avalanche.data.periods.map(p => [p.date, p]))
          for (const p of result.data.periods) {
            existing.set(p.date, p)
          }
          const merged = [...existing.values()].sort((a, b) => a.date.localeCompare(b.date))
          this.avalanche = {
            ...this.avalanche,
            data: { ...this.avalanche.data, periods: merged },
          }
        } else {
          this.avalanche = result
        }
      }
      this.avyFetchedDates.add(date)
    } catch {
      // Non-fatal — carousel will show "no data" for this date
    } finally {
      this.avyDayLoading = false
    }
  }

  private async prefetchAvyDay(currentDate: string): Promise<void> {
    const idx = this.avyDateRange.indexOf(currentDate)
    if (idx < 0) return

    // Prefetch adjacent days
    const adjacent = [this.avyDateRange[idx - 1], this.avyDateRange[idx + 1]].filter(
      (d): d is string => !!d && !this.avyFetchedDates.has(d)
    )
    for (const date of adjacent) {
      void this.loadAvyDay(date)
    }
  }

  private static shiftDate(isoDate: string, days: number): string {
    const d = new Date(isoDate + 'T12:00:00Z')
    d.setUTCDate(d.getUTCDate() + days)
    return d.toISOString().slice(0, 10)
  }

  private async refreshConditions(): Promise<void> {
    await Promise.all([this.loadWeather(true), this.loadAvalanche(true)])
  }

  private async loadEmergencyServices(): Promise<void> {
    if (this.emergencyServices) return
    const coords = this.resolvePlanCoordinates()
    if (!coords) return

    this.emergencyLoading = true
    try {
      const [lat, lon] = coords

      const isSheriff = (f: AnyEmergencyFacility): f is LawEnforcement =>
        f.type === 'law-enforcement' &&
        (f.agencyType === 'SHERIFF' || f.name.toUpperCase().includes('SHERIFF'))

      const isEMS = (f: AnyEmergencyFacility): f is FireStation =>
        f.type === 'fire-station' && f.hasEMS

      const [allHospitals, sheriffs, fireEMS, rangerStations] = await Promise.all([
        emergencyDataService.queryNearest('hospitals', lat, lon, 10),
        emergencyDataService.queryNearestFiltered('law-enforcement', lat, lon, isSheriff, 3),
        emergencyDataService.queryNearestFiltered('fire-stations', lat, lon, isEMS, 3),
        emergencyDataService.queryNearest('usfs-offices', lat, lon, 3),
      ])

      const hospitals = allHospitals.slice(0, 2)

      const traumaCenters = allHospitals
        .filter(f => {
          const h = f as Hospital
          return h.traumaLevel !== 'NOT_DESIGNATED' && h.traumaLevel !== 'UNKNOWN'
        })
        .slice(0, 2)

      const helipadHospitals = allHospitals.filter(f => (f as Hospital).helipad).slice(0, 2)

      this.emergencyServices = {
        sheriffs,
        hospitals,
        traumaCenters,
        helipadHospitals,
        fireEMS,
        rangerStations,
      }
    } catch (err) {
      console.error('[plan-detail] Failed to load emergency services:', err)
    } finally {
      this.emergencyLoading = false
    }
  }

  private resolvePlanDateRange(): { startDate: string; endDate: string } | null {
    const p = this.plan!
    if (p.times.length === 0) return null
    const dates = p.times.map(t => t.datetime.slice(0, 10)).sort()
    return { startDate: dates[0], endDate: dates[dates.length - 1] }
  }

  private resolvePlanCoordinates(): [number, number] | null {
    // Prefer the centroid of all route features
    if (this.routeFeatures.length > 0) {
      const fc = featureCollection(this.routeFeatures.map(f => turfFeature(f.geometry)))
      const [lng, lat] = center(fc).geometry.coordinates
      return [lat, lng]
    }

    // Fall back to plan location
    const p = this.plan!
    if (p.location?.coordinates) return p.location.coordinates
    return null
  }

  private static skyConditionIcon(condition: SkyCondition): string {
    switch (condition) {
      case 'clear':
        return 'clear_day'
      case 'mostly-clear':
        return 'partly_cloudy_day'
      case 'partly-cloudy':
        return 'partly_cloudy_day'
      case 'overcast':
        return 'cloud'
      case 'fog':
        return 'foggy'
      case 'drizzle':
        return 'rainy_light'
      case 'rain':
        return 'rainy_heavy'
      case 'freezing-rain':
        return 'weather_mix'
      case 'snow':
        return 'weather_snowy'
      case 'snow-showers':
        return 'snowing'
      case 'rain-showers':
        return 'rainy'
      case 'thunderstorm':
        return 'thunderstorm'
    }
  }

  private static skyConditionLabel(condition: SkyCondition): string {
    switch (condition) {
      case 'clear':
        return 'Clear'
      case 'mostly-clear':
        return 'Mostly Clear'
      case 'partly-cloudy':
        return 'Partly Cloudy'
      case 'overcast':
        return 'Overcast'
      case 'fog':
        return 'Fog'
      case 'drizzle':
        return 'Drizzle'
      case 'rain':
        return 'Rain'
      case 'freezing-rain':
        return 'Freezing Rain'
      case 'snow':
        return 'Snow'
      case 'snow-showers':
        return 'Snow Showers'
      case 'rain-showers':
        return 'Rain Showers'
      case 'thunderstorm':
        return 'Thunderstorm'
    }
  }

  private static lightningRiskColor(risk: LightningRisk): string {
    switch (risk) {
      case 'high':
        return '#ef4444'
      case 'moderate':
        return '#eab308'
      case 'low':
        return '#9ca3af'
    }
  }

  private aggregateHourly(
    hours: HourlyForecast[],
    granularity: 1 | 3 | 6
  ): Array<{
    label: string
    temp: number
    sky: SkyCondition
    precip: number
    snow: number
    cape: number
  }> {
    if (granularity === 1) {
      return hours.map(h => ({
        label: new Date(h.time).toLocaleTimeString('en-US', { hour: 'numeric' }),
        temp: h.temperature,
        sky: h.skyCondition,
        precip: h.precipAmount,
        snow: h.snowfall,
        cape: h.cape,
      }))
    }

    const blocks: Array<{
      label: string
      temp: number
      sky: SkyCondition
      precip: number
      snow: number
      cape: number
    }> = []

    for (let i = 0; i < hours.length; i += granularity) {
      const chunk = hours.slice(i, i + granularity)
      if (chunk.length === 0) continue
      const midIdx = Math.min(Math.floor(granularity / 2), chunk.length - 1)
      const mid = chunk[midIdx]
      blocks.push({
        label: new Date(mid.time).toLocaleTimeString('en-US', { hour: 'numeric' }),
        temp: mid.temperature,
        sky: mid.skyCondition,
        precip: chunk.reduce((s, h) => s + h.precipAmount, 0),
        snow: chunk.reduce((s, h) => s + h.snowfall, 0),
        cape: Math.max(...chunk.map(h => h.cape)),
      })
    }
    return blocks
  }

  private toggleWeatherChip(id: string): void {
    this.activeWeatherChips = new Set([id])
  }

  private toggleWeatherDay(date: string): void {
    const next = new Set(this.expandedWeatherDays)
    if (next.has(date)) next.delete(date)
    else next.add(date)
    this.expandedWeatherDays = next
  }

  private static readonly CHART_COLORS = [
    '#f97316',
    '#3b82f6',
    '#a855f7',
    '#22c55e',
    '#ec4899',
    '#14b8a6',
  ]

  private renderWeatherDayDetail(d: DailyForecast, dayHours: HourlyForecast[]): unknown {
    if (dayHours.length === 0) {
      return html`<div class="weather-day-detail">
        <div class="weather-day-detail-empty">No hourly data available</div>
      </div>`
    }

    const sources: Array<{ id: string; label: string; hourly: HourlyForecast[]; color: string }> =
      []
    const pointsWithData = this.elevationWeatherPoints.filter(p => p.data)

    if (pointsWithData.length > 0) {
      for (let i = 0; i < pointsWithData.length; i++) {
        const p = pointsWithData[i]
        const hrs = p.data!.hourly.filter(h => h.time.startsWith(d.date))
        if (hrs.length === 0) continue
        sources.push({
          id: p.id,
          label: `${p.label} (${p.elevationFt.toLocaleString()} ft)`,
          hourly: hrs,
          color: PlanDetail.CHART_COLORS[i % PlanDetail.CHART_COLORS.length],
        })
      }
    }

    if (sources.length === 0) {
      sources.push({
        id: 'area',
        label: 'Area',
        hourly: (this.weather?.data.hourly ?? []).filter(h => h.time.startsWith(d.date)),
        color: PlanDetail.CHART_COLORS[0],
      })
    }

    const ensembleSpreads =
      this.weather?.ensemble?.spreads.filter(s => s.time.startsWith(d.date)) ?? []

    // DEBUG: chart data for expanded day
    console.group(`[weather-chart] ${d.date}`)
    console.log(
      'sources:',
      sources.map(s => ({ id: s.id, label: s.label, hourlyCount: s.hourly.length }))
    )
    console.log(
      'ensembleSpreads for day:',
      ensembleSpreads.length,
      ensembleSpreads.length > 0
        ? {
            times: ensembleSpreads.map(s => s.time),
            sampleTemp: ensembleSpreads[0]?.temperature,
            sampleWind: ensembleSpreads[0]?.wind,
          }
        : '(none)'
    )
    console.log(
      'cape values:',
      sources[0]?.hourly.slice(0, 6).map(h => ({ time: h.time, cape: h.cape }))
    )
    console.groupEnd()

    return html`
      <div class="weather-day-detail">
        <div class="weather-day-detail-meta">
          <span
            >Sunrise
            ${new Date(d.sunrise).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
            })}</span
          >
          <span
            >Sunset
            ${new Date(d.sunset).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
            })}</span
          >
          <span>UV ${d.uvIndexMax}</span>
        </div>
        <weather-day-chart
          .sources=${sources}
          .ensembleSpreads=${ensembleSpreads}
          .planTimes=${this.planTimesForDay(d.date)}
          .unitSystem=${this.units}
          .sunrise=${d.sunrise}
          .sunset=${d.sunset}
        ></weather-day-chart>
      </div>
    `
  }

  private detectOverflow(): void {
    const entries = this.shadowRoot?.querySelectorAll('.timeline-entry')
    entries?.forEach(entry => {
      if (entry.classList.contains('expanded')) return
      const content = entry.querySelector('.timeline-entry-content')
      if (content && content.scrollHeight > content.clientHeight) {
        entry.classList.add('overflowing')
      } else {
        entry.classList.remove('overflowing')
      }
    })
    // Check individual subtitle lines for horizontal text overflow
    const subtitles = this.shadowRoot?.querySelectorAll('.time-subtitle')
    subtitles?.forEach(el => {
      const entry = el.closest('.timeline-entry')
      if (entry?.classList.contains('expanded')) return
      if (el.scrollWidth > el.clientWidth) {
        el.classList.add('text-overflow')
      } else {
        el.classList.remove('text-overflow')
      }
    })
  }

  private async loadPlan(): Promise<void> {
    if (!this.planId) return
    const raw = await db.plans.get(this.planId)
    if (!raw) {
      this.plan = null
      return
    }
    // Migrate legacy string gear fields to arrays
    if (!Array.isArray(raw.teamGear)) {
      ;(raw as Record<string, unknown>).teamGear = []
    }
    if (!Array.isArray(raw.individualGear)) {
      ;(raw as Record<string, unknown>).individualGear = []
    }
    this.plan = raw
    await this.loadPlanFeatures()
    void this.loadRelated()
    void this.loadMembers()
    void this.loadEmergencyServices()
  }

  private async loadMembers(): Promise<void> {
    if (!this.planId) return
    try {
      let edges = await edgeService.getBySubject(nodeRef('plan', this.planId), 'has-member')

      // Backfill: if the plan creator has no membership edge, create one
      const creatorId = this.plan?.createdBy
      if (creatorId && !edges.some(e => e.object === `user:${creatorId}`)) {
        await planStorage.addMember(this.planId, creatorId, 'leader', 'active')
        edges = await edgeService.getBySubject(nodeRef('plan', this.planId), 'has-member')
      }

      const auth = authService.getState()
      const seen = new Set<string>()
      const members = (
        await Promise.all(
          edges.map(async (edge: Edge) => {
            const userId = edge.object.split(':')[1]
            if (seen.has(userId)) return null
            seen.add(userId)
            const props = (edge.properties ?? {}) as Record<string, unknown>
            const user = await db.users.get(userId)
            const isCurrentUser = userId === auth.userId
            const displayName =
              user?.displayName ??
              (isCurrentUser ? auth.displayName : undefined) ??
              userId.slice(0, 8)
            const pic = user?.picture ?? (isCurrentUser ? auth.picture : undefined)
            return {
              userId,
              displayName,
              ...(pic ? { picture: pic } : {}),
              role: (props.role as string | undefined) ?? 'member',
              ...(props.sharedContact
                ? {
                    sharedContact: props.sharedContact as NonNullable<
                      (typeof this.members)[0]['sharedContact']
                    >,
                  }
                : {}),
            }
          })
        )
      ).filter(m => m !== null)
      this.members = members
    } catch {
      this.members = []
    }
  }

  private get currentUserRole(): string | null {
    const userId = authService.getState().userId
    if (!userId) return null
    const member = this.members.find(m => m.userId === userId)
    if (member) return member.role
    if (this.plan?.createdBy === userId) return 'leader'
    return null
  }

  private get isLeader(): boolean {
    return this.currentUserRole === 'leader'
  }

  private get isSoleLeader(): boolean {
    if (!this.isLeader) return false
    return this.members.filter(m => m.role === 'leader').length === 1
  }

  private async handleLeavePlan(): Promise<void> {
    const userId = authService.getState().userId
    if (!userId || !this.planId) return

    if (this.isSoleLeader) {
      showToast('error', 'You must promote another member to leader before leaving')
      return
    }

    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Leave Plan'
      dialog.message = `Leave "${this.plan?.name}"? You'll need a new invite to rejoin.`
      dialog.confirmLabel = 'Leave'
      const confirmed = await dialog.show()
      dialog.confirmLabel = 'Delete'
      if (!confirmed) return
    }

    await planStorage.removeMember(this.planId, userId)
    showToast('success', 'Left plan')
    router.navigate('/plans')
  }

  private async loadRelated(): Promise<void> {
    if (!this.planId) return
    try {
      // Self-healing: link any unassociated in-window activities whose track
      // overlaps this plan's route before listing (catches e.g. a day-2 track
      // that started off-route and missed the at-stop match).
      await backfillPlanAssociations(this.planId)
      const activityIds = await planStorage.getActivityIds(this.planId)
      const activities = await Promise.all(activityIds.map((id: string) => db.activities.get(id)))
      this.linkedRoute = null
      this.linkedActivities = activities.filter((a): a is Activity => a !== undefined)
    } catch {
      this.linkedRoute = null
      this.linkedActivities = []
    }
  }

  private async loadPlanFeatures(): Promise<void> {
    if (!this.planId) return
    try {
      this.routeFeatures = await featureStorage.getFeaturesForEntity('plan', this.planId)
    } catch {
      this.routeFeatures = []
    }
    this.computeRouteChartData()
  }

  /** Build per-feature elevation profiles and aspect counts from line features */
  private async computeRouteChartData(): Promise<void> {
    const gen = ++this.chartGeneration
    const lineFeatures = this.routeFeatures.filter(
      f =>
        f.geometry.type === 'LineString' &&
        (f.geometry as GeoJSON.LineString).coordinates.length >= 2
    )

    const result = await Promise.all(
      lineFeatures.map(async (feat, fi) => {
        const coords = (feat.geometry as GeoJSON.LineString).coordinates
        const points: ProfilePoint[] = []
        const aspectCounts: AspectCounts = [0, 0, 0, 0, 0, 0, 0, 0]
        let cumDist = 0

        const needsDem = coords.some(c => c.length < 3)

        // Sample slope aspects and (if needed) DEM elevations in parallel
        const aspectPromises = coords.map(c => elevationPointService.getAspect(c[1], c[0]))
        const elevPromises = needsDem
          ? coords.map(c =>
              c.length >= 3 ? Promise.resolve(c[2]) : elevationPointService.getElevation(c[1], c[0])
            )
          : null
        const [aspects, demElevations] = await Promise.all([
          Promise.all(aspectPromises),
          elevPromises ? Promise.all(elevPromises) : Promise.resolve(null),
        ])

        for (let i = 0; i < coords.length; i++) {
          if (i > 0) {
            cumDist += PlanDetail.haversineM(coords[i - 1], coords[i])
          }
          const elevation = coords[i].length >= 3 ? coords[i][2] : (demElevations?.[i] ?? 0)
          points.push({ distance: cumDist, elevation })

          const aspect = aspects[i]
          if (aspect !== null) {
            const bucket = Math.round(aspect / 45) % 8
            aspectCounts[bucket]++
          }
        }

        const color = feat.style?.lineColor ?? '#3f5a7a'
        const name = feat.name || `Route ${fi + 1}`

        return {
          name,
          profile: { name, points, color },
          aspectCounts,
        }
      })
    )
    if (gen !== this.chartGeneration) return
    this.routeChartData = result
  }

  /** Haversine distance in meters between two [lng, lat, ...] coords */
  private static haversineM(a: GeoJSON.Position, b: GeoJSON.Position): number {
    const toRad = (d: number): number => (d * Math.PI) / 180
    const R = 6371000
    const dLat = toRad(b[1] - a[1])
    const dLon = toRad(b[0] - a[0])
    const lat1 = toRad(a[1])
    const lat2 = toRad(b[1])
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
  }

  private async handleDelete(): Promise<void> {
    if (!this.plan) return
    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Delete Plan'
      dialog.message = `Delete "${this.plan.name}"? This cannot be undone.`
      const confirmed = await dialog.show()
      if (!confirmed) return
    }
    await planStorage.deletePlan(this.plan.id)
    router.navigate('/plans')
  }

  private async handleReportMember(userId: string, displayName: string): Promise<void> {
    await import('../common/report-dialog.js')
    let dialog = document.body.querySelector('report-dialog')
    if (!dialog) {
      dialog = document.createElement('report-dialog')
      document.body.appendChild(dialog)
    }
    await dialog.show('user', userId, displayName)
  }

  private async handleBlockMember(userId: string, displayName: string): Promise<void> {
    await import('../common/block-dialog.js')
    let dialog = document.body.querySelector('block-dialog')
    if (!dialog) {
      dialog = document.createElement('block-dialog')
      document.body.appendChild(dialog)
    }

    const action = await dialog.show(userId, displayName)

    if (action) {
      for (const planId of action.plansToRemoveFrom) {
        try {
          await planStorage.removeMember(planId, userId)
        } catch {
          /* best effort */
        }
      }
      for (const planId of action.plansToLeave) {
        const currentUserId = authService.getState().userId
        if (currentUserId) {
          try {
            await planStorage.removeMember(planId, currentUserId)
          } catch {
            /* best effort */
          }
        }
      }
      for (const teamId of action.teamsToRemoveFrom) {
        try {
          const { teamMemberStorage } = await import('../../storage/team-storage.js')
          await teamMemberStorage.removeMember(teamId, userId)
        } catch {
          /* best effort */
        }
      }
      for (const teamId of action.teamsToLeave) {
        const currentUserId = authService.getState().userId
        if (currentUserId) {
          try {
            const { teamMemberStorage } = await import('../../storage/team-storage.js')
            await teamMemberStorage.removeMember(teamId, currentUserId)
          } catch {
            /* best effort */
          }
        }
      }
      this.requestUpdate()
    }
  }

  private toggleEntry(e: Event): void {
    const entry = e.currentTarget as HTMLElement
    const content = entry.querySelector('.timeline-entry-content')
    const isExpanded = entry.classList.toggle('expanded')
    if (!isExpanded && content) {
      if (content.scrollHeight > content.clientHeight) {
        entry.classList.add('overflowing')
      } else {
        entry.classList.remove('overflowing')
      }
    }
  }

  static mapsUrl(loc: PlanLocation): string {
    if (loc.coordinates)
      return `https://www.google.com/maps/search/?api=1&query=${loc.coordinates[0]},${loc.coordinates[1]}`
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.name)}`
  }

  private formatTime(d: string): string {
    return new Date(d).toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  private getDateSummary(): string | null {
    const p = this.plan!
    if (p.times.length === 0) return null

    const sorted = p.times.map(t => new Date(t.datetime).getTime()).sort((a, b) => a - b)
    if (sorted.length === 0) return null

    const currentYear = new Date().getFullYear()
    const fmt = (ms: number): string => {
      const d = new Date(ms)
      const opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }
      if (d.getFullYear() !== currentYear) {
        opts.year = 'numeric'
      }
      return d.toLocaleDateString('en-US', opts)
    }

    const uniqueDates = new Set(sorted.map(ms => new Date(ms).toDateString()))
    return uniqueDates.size === 1
      ? fmt(sorted[0])
      : `${fmt(sorted[0])} – ${fmt(sorted[sorted.length - 1])}`
  }

  private renderRelated(): unknown {
    if (!this.linkedRoute && this.linkedActivities.length === 0) return nothing
    return html`
      <div class="related-row">
        ${this.linkedRoute
          ? html`
              <button
                class="related-chip"
                @click=${() => router.navigate(`/routes/${this.linkedRoute!.id}`)}
              >
                <span class="material-icons">alt_route</span>
                Route: ${this.linkedRoute.name}
              </button>
            `
          : nothing}
        ${this.linkedActivities.map(
          a => html`
            <button class="related-chip" @click=${() => router.navigate(`/activities/${a.id}`)}>
              <span class="material-icons">directions_walk</span>
              ${a.name}
            </button>
          `
        )}
      </div>
    `
  }

  private renderOverview(): unknown {
    const p = this.plan!
    const hasLocation = !!p.location
    const hasDescription = !!p.descriptionRich || !!p.description
    const dateSummary = this.getDateSummary()
    const hasRouteFeatures = this.routeFeatures.length > 0
    const hasMap = hasRouteFeatures || (hasLocation && (p.location!.bounds ?? p.location!.name))
    const hasTimes = p.times.length > 0

    if (!hasMap && !hasLocation && !hasDescription && !dateSummary && !hasTimes) return nothing

    return html`
      <div class="section overview-section">
        ${hasMap
          ? html`<div class="overview-map">
              ${keyed(
                this.mapVersion,
                html`<trip-map-preview
                  .planId=${this.planId}
                  .location=${p.location}
                  style="height: 100%; min-height: 200px;"
                ></trip-map-preview>`
              )}
            </div>`
          : nothing}
        ${dateSummary || hasLocation
          ? html`<div class="meta">
              ${dateSummary
                ? html`<span class="material-icons">calendar_today</span
                    ><span class="meta-date">${dateSummary}</span>`
                : nothing}
              ${dateSummary && hasLocation ? html`<span class="meta-separator">·</span>` : nothing}
              ${hasLocation
                ? html`<span class="material-icons">place</span
                    ><span class="meta-location"
                      ><a
                        class="maps-link"
                        href=${PlanDetail.mapsUrl(p.location!)}
                        target="_blank"
                        rel="noopener"
                        >${p.location!.name}</a
                      ></span
                    >`
                : nothing}
            </div>`
          : nothing}
        ${hasDescription
          ? p.descriptionRich
            ? html`<rich-text-renderer .content=${p.descriptionRich}></rich-text-renderer>`
            : html`<div class="text-block">${p.description}</div>`
          : nothing}
        ${this.renderRelated()} ${hasTimes ? this.renderTimelineContent() : nothing}
        ${this.routeChartData.map(
          rd => html`
            <div class="route-charts">
              <h4 class="route-chart-heading">${rd.name}</h4>
              <div class="route-chart-row">
                <div class="route-chart elevation-chart">
                  <elevation-profile
                    .profiles=${[rd.profile]}
                    .unitSystem=${this.units}
                  ></elevation-profile>
                </div>
                ${rd.aspectCounts.some(c => c > 0)
                  ? html`<div class="route-chart aspect-chart">
                      <track-aspect-rose .counts=${rd.aspectCounts}></track-aspect-rose>
                    </div>`
                  : nothing}
              </div>
            </div>
          `
        )}
      </div>
    `
  }

  private renderEmergencyContacts(): unknown {
    const contacts = this.plan?.emergencyContacts
    if (!contacts || contacts.length === 0) return nothing

    return html`
      <div class="section">
        <h2 class="section-title">Emergency Contacts</h2>
        ${contacts.map(
          c => html`
            <div class="emergency-facility-row">
              <div class="emergency-facility-details">
                <div class="emergency-facility-name">${c.name}</div>
                <div class="emergency-facility-meta">
                  ${c.relationship
                    ? html`<span class="emergency-type-label">${c.relationship}</span>`
                    : nothing}
                  ${c.phone
                    ? html`<a class="emergency-phone-inline" href="tel:${c.phone}">${c.phone}</a>`
                    : nothing}
                </div>
                <div
                  style="font-size: 11px; color: var(--cairn-text-secondary-color, #64748b); margin-top: 2px;"
                >
                  ${typeof c.source === 'string'
                    ? 'Custom'
                    : c.source.type === 'team'
                      ? `Team: ${c.source.teamName}`
                      : `Member: ${c.source.displayName}`}
                </div>
              </div>
              ${c.phone
                ? html`<a class="emergency-phone" href="tel:${c.phone}" title="Call ${c.name}">
                    <span class="material-icons">call</span>
                  </a>`
                : nothing}
            </div>
          `
        )}
      </div>
    `
  }

  private renderEmergencyServices(): unknown {
    const coords = this.resolvePlanCoordinates()
    if (!coords) return nothing

    if (this.emergencyLoading) {
      return html`<div class="section">
        <h2 class="section-title">Emergency Services</h2>
        <div class="weather-loading">Loading nearby services...</div>
      </div>`
    }

    const es = this.emergencyServices
    if (!es) return nothing

    const primary = [es.sheriffs[0], es.fireEMS[0], es.rangerStations[0]].filter(Boolean)
    if (primary.length === 0) return nothing

    const extra = [
      ...es.sheriffs.slice(1),
      ...es.hospitals,
      ...es.traumaCenters,
      ...es.helipadHospitals,
      ...es.fireEMS.slice(1),
      ...es.rangerStations.slice(1),
    ]
    const deduped = this.dedupeEmergency(extra, primary)

    return html`
      <div class="section">
        <h2 class="section-title">Emergency Services</h2>
        ${primary.map(f => this.renderEmergencyFacility(f))}
        ${deduped.length > 0
          ? html`
              <button
                class="emergency-expand"
                @click=${() => {
                  this.emergencyExpanded = !this.emergencyExpanded
                }}
              >
                <span class="material-icons"
                  >${this.emergencyExpanded ? 'expand_less' : 'expand_more'}</span
                >
                ${this.emergencyExpanded ? 'Show less' : `${deduped.length} more nearby`}
              </button>
              ${this.emergencyExpanded
                ? deduped.map(f => this.renderEmergencyFacility(f))
                : nothing}
            `
          : nothing}
      </div>
    `
  }

  private dedupeEmergency(
    extra: EmergencyFacilityWithDistance[],
    exclude: EmergencyFacilityWithDistance[]
  ): EmergencyFacilityWithDistance[] {
    const seen = new Set(exclude.map(f => f.id ?? `${f.lat}-${f.lon}`))
    const result: EmergencyFacilityWithDistance[] = []
    for (const f of extra) {
      const key = f.id ?? `${f.lat}-${f.lon}`
      if (seen.has(key)) continue
      seen.add(key)
      result.push(f)
    }
    return result.sort((a, b) => a.distanceKm - b.distanceKm)
  }

  private renderEmergencyFacility(f: EmergencyFacilityWithDistance): unknown {
    const distMeters = f.distanceKm * 1000
    const dist = convertDistance(distMeters, this.units)
    const label = this.emergencyTypeLabel(f)
    const addressParts = [f.address, f.city && f.state ? `${f.city}, ${f.state}` : null]
      .filter(Boolean)
      .join(', ')
    const mapsQuery = addressParts || `${f.lat},${f.lon}`
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsQuery)}`
    return html`
      <div class="emergency-facility-row">
        <div class="emergency-facility-details">
          <div class="emergency-facility-name">${f.name}</div>
          <div class="emergency-facility-meta">
            <span class="emergency-dist">${dist}</span>
            ${label ? html`<span class="emergency-type-label">${label}</span>` : nothing}
            ${f.phone
              ? html`<a class="emergency-phone-inline" href="tel:${f.phone}">${f.phone}</a>`
              : nothing}
          </div>
          ${addressParts
            ? html`<a
                class="emergency-address-link"
                href="${mapsUrl}"
                target="_blank"
                rel="noopener"
                >${addressParts}</a
              >`
            : nothing}
        </div>
        ${f.phone
          ? html`<a class="emergency-phone" href="tel:${f.phone}" title="Call ${f.name}">
              <span class="material-icons">call</span>
            </a>`
          : nothing}
      </div>
    `
  }

  private emergencyTypeLabel(f: AnyEmergencyFacility): string {
    switch (f.type) {
      case 'law-enforcement': {
        const le = f
        if (le.agencyType === 'SHERIFF') return 'Sheriff'
        return 'Law Enforcement'
      }
      case 'fire-station':
        return f.hasEMS ? 'Fire & EMS' : 'Fire'
      case 'usfs-office':
        return 'USFS Ranger'
      case 'hospital': {
        const h = f
        const tags: string[] = ['Hospital']
        if (h.traumaLevel !== 'NOT_DESIGNATED' && h.traumaLevel !== 'UNKNOWN')
          tags.push(h.traumaLevel.replace('_', ' '))
        if (h.helipad) tags.push('Helipad')
        return tags.join(' · ')
      }
      default:
        return ''
    }
  }

  private renderMembers(): unknown {
    const currentUserId = authService.getState().userId

    return html`
      <div class="section members-section">
        <div class="members-header">
          <h3 class="members-heading">
            <span class="material-icons">group</span>
            Members (${this.members.length})
          </h3>
        </div>

        <div style="display: flex; flex-direction: column;">
          ${this.members
            .filter(m => m.userId === currentUserId || !blockStore.isBlocked(m.userId))
            .map(m => {
              const isCurrentUser = m.userId === currentUserId
              const isExpanded = this.expandedMemberId === m.userId
              const hasContact =
                m.sharedContact?.email ??
                m.sharedContact?.phone ??
                m.sharedContact?.emergencyContact
              const auth = authService.getState()
              const picture = m.picture ?? (isCurrentUser ? auth.picture : undefined)

              return html`
                <div
                  class="member-item"
                  @click=${() => {
                    this.expandedMemberId = isExpanded ? null : m.userId
                  }}
                >
                  ${picture
                    ? html`<img class="member-avatar" src="${picture}" alt="" />`
                    : html`<div class="member-avatar-placeholder">
                        ${m.displayName.charAt(0).toUpperCase()}
                      </div>`}
                  <div class="member-info">
                    <div class="member-name">
                      ${m.displayName}
                      ${isCurrentUser ? html` <span class="member-you">(you)</span>` : nothing}
                    </div>
                  </div>
                  <span class="role-badge ${m.role}">${m.role}</span>
                </div>
                ${isExpanded
                  ? html`
                      <div class="member-expanded">
                        ${isCurrentUser
                          ? html`
                              <md-text-button
                                @click=${(e: Event) => {
                                  e.stopPropagation()
                                  void this.openContactShareDialog()
                                }}
                                style="--md-text-button-label-text-color: var(--cairn-primary-color, #3b82f6);"
                              >
                                <span
                                  class="material-icons"
                                  slot="icon"
                                  style="font-family: 'Material Symbols Outlined'"
                                  >share</span
                                >
                                Manage shared info
                              </md-text-button>
                            `
                          : nothing}
                        ${!isCurrentUser && hasContact
                          ? html`
                              ${m.sharedContact?.email
                                ? html`<div class="member-contact-row">
                                    <span class="material-icons">email</span> ${m.sharedContact
                                      .email}
                                  </div>`
                                : nothing}
                              ${m.sharedContact?.phone
                                ? html`<div class="member-contact-row">
                                    <span class="material-icons">phone</span> ${m.sharedContact
                                      .phone}
                                  </div>`
                                : nothing}
                              ${m.sharedContact?.emergencyContact
                                ? html`<div class="member-contact-row">
                                    <span class="material-icons">emergency</span>
                                    <strong>Emergency:</strong>
                                    ${m.sharedContact.emergencyContact.name}
                                    (${m.sharedContact.emergencyContact.relationship}) —
                                    ${m.sharedContact.emergencyContact.phone}
                                  </div>`
                                : nothing}
                            `
                          : nothing}
                        ${!isCurrentUser && !hasContact
                          ? html`<div class="member-contact-row" style="opacity: 0.5;">
                              No shared contact info
                            </div>`
                          : nothing}
                      </div>
                      <div class="member-actions">
                        ${isCurrentUser
                          ? html`
                              <md-text-button
                                @click=${(e: Event) => {
                                  e.stopPropagation()
                                  void this.handleLeavePlan()
                                }}
                                style="--md-text-button-label-text-color: #dc2626;"
                                >Leave Plan</md-text-button
                              >
                            `
                          : nothing}
                        ${!isCurrentUser
                          ? html`
                              <md-icon-button
                                title="Report ${m.displayName}"
                                @click=${(e: Event) => {
                                  e.stopPropagation()
                                  void this.handleReportMember(m.userId, m.displayName)
                                }}
                                aria-label="Report user"
                              >
                                <span
                                  class="material-icons"
                                  style="font-family: 'Material Symbols Outlined'"
                                  >flag</span
                                >
                              </md-icon-button>
                              <md-icon-button
                                title="Block ${m.displayName}"
                                @click=${(e: Event) => {
                                  e.stopPropagation()
                                  void this.handleBlockMember(m.userId, m.displayName)
                                }}
                                aria-label="Block user"
                              >
                                <span
                                  class="material-icons"
                                  style="font-family: 'Material Symbols Outlined'"
                                  >block</span
                                >
                              </md-icon-button>
                            `
                          : nothing}
                      </div>
                    `
                  : nothing}
              `
            })}
        </div>
      </div>
      <contact-share-dialog></contact-share-dialog>
    `
  }

  private async openContactShareDialog(): Promise<void> {
    const userId = authService.getState().userId
    if (!userId || !this.planId) return

    const profile = await userStorage.getUserProfile(userId)
    const user = profile.data

    const dialog = this.shadowRoot?.querySelector(
      'contact-share-dialog'
    ) as ContactShareDialog | null
    if (!dialog) return

    const contactData: Record<string, unknown> = {}
    if (user?.email) contactData.email = user.email
    if (user?.phone) contactData.phone = user.phone
    if (user?.emergencyContact) contactData.emergencyContact = user.emergencyContact
    const choices = await dialog.show(
      contactData as {
        email?: string
        phone?: string
        emergencyContact?: { name: string; phone: string; relationship: string }
      }
    )

    if (choices) {
      await planStorage.updateMemberContact(this.planId, userId, choices)
      await this.loadMembers()
    }
  }

  private static readonly TAG_ICONS: Record<string, string> = {
    meeting: 'group',
    start: 'play_arrow',
    turnaround: 'u_turn_left',
    return: 'home',
    overdue: 'warning',
    custom: 'schedule',
  }

  private planTimesForDay(date: string): ChartPlanTime[] {
    if (!this.plan) return []
    return this.plan.times
      .filter(t => t.datetime.startsWith(date))
      .map(t => ({
        datetime: t.datetime,
        icon: t.location?.icon ?? PlanDetail.TAG_ICONS[t.tag] ?? 'schedule',
        tag: t.tag,
      }))
  }

  /** Group times by calendar date and render the timeline rail. */
  private renderTimelineContent(): unknown {
    const p = this.plan!

    // Group entries by calendar date
    const groups = new Map<string, typeof p.times>()
    for (const t of p.times) {
      const dateKey = new Date(t.datetime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
      const group = groups.get(dateKey)
      if (group) {
        group.push(t)
      } else {
        groups.set(dateKey, [t])
      }
    }

    return html`
      <div class="timeline">
        ${[...groups.entries()].map(
          ([dateLabel, entries]) => html`
            <div class="timeline-date-divider"></div>
            ${entries.map(
              (t, i) => html`
                <div class="timeline-entry" @click=${this.toggleEntry}>
                  ${i === 0
                    ? html`<div class="timeline-date-label">
                        ${(() => {
                          const dt = new Date(dateLabel)
                          return html`${dt.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}<br />${dt.toLocaleDateString('en-US', { weekday: 'short' })}`
                        })()}
                      </div>`
                    : nothing}
                  <div class="timeline-node ${t.tag}">
                    <span class="material-icons"
                      >${t.location?.icon ?? PlanDetail.TAG_ICONS[t.tag]}</span
                    >
                  </div>
                  <div class="timeline-entry-content">
                    <div class="timeline-entry-header">
                      <span class="time-name">${t.name}</span>
                      <span class="time-value">${this.formatTime(t.datetime)}</span>
                    </div>
                    ${t.location
                      ? html`<div class="time-subtitle">
                          <a
                            class="maps-link"
                            href=${PlanDetail.mapsUrl(t.location)}
                            target="_blank"
                            rel="noopener"
                            ><span class="material-icons">place</span>${t.location.name}</a
                          >
                        </div>`
                      : nothing}
                    ${t.note ? html`<div class="time-subtitle">${t.note}</div>` : nothing}
                  </div>
                </div>
              `
            )}
          `
        )}
      </div>
    `
  }

  private renderPackItem(item: PlanPackItem, list: 'team' | 'individual'): unknown {
    return html`
      <md-list-item class=${item.packed ? 'packed' : ''}>
        <md-checkbox
          slot="start"
          touch-target="wrapper"
          .checked=${item.packed}
          @change=${(e: Event) =>
            this.togglePacked(list, item.id, (e.target as HTMLInputElement).checked)}
        ></md-checkbox>
        <span class="pack-item-name" slot="headline"
          >${item.name}${item.quantity != null && item.quantity > 1
            ? html`<span class="pack-item-qty">x${item.quantity}</span>`
            : nothing}</span
        >
        ${item.description
          ? html`<span slot="supporting-text">${item.description}</span>`
          : nothing}
      </md-list-item>
    `
  }

  private static sortedByPacked(items: PlanPackItem[]): PlanPackItem[] {
    return [...items].sort((a, b) => Number(a.packed) - Number(b.packed))
  }

  private togglePacked(list: 'team' | 'individual', itemId: string, packed: boolean): void {
    if (!this.plan) return
    const field = list === 'team' ? 'teamGear' : 'individualGear'
    const updated = this.plan[field].map((i: PlanPackItem) =>
      i.id === itemId ? { ...i, packed } : i
    )
    void planStorage.updatePlan(this.plan.id, { [field]: updated })
    this.plan = { ...this.plan, [field]: updated }
  }

  private toggleSection(section: string): void {
    const next = new Set(this.collapsedSections)
    if (next.has(section)) {
      next.delete(section)
    } else {
      next.add(section)
    }
    this.collapsedSections = next
  }

  private renderGearGroup(
    groupName: string | null,
    items: PlanPackItem[],
    list: 'team' | 'individual'
  ): unknown {
    if (groupName === null) {
      // Ungrouped items — render in their own md-list, indented under section
      return html`
        <div class="gear-group">
          <md-list>
            ${repeat(
              PlanDetail.sortedByPacked(items),
              item => item.id,
              item => this.renderPackItem(item, list)
            )}
          </md-list>
        </div>
      `
    }

    const sectionKey = `${list}:${groupName}`
    const collapsed = this.collapsedSections.has(sectionKey)
    const packedCount = items.filter(i => i.packed).length

    return html`
      <div class="gear-group">
        <div class="gear-group-label" @click=${() => this.toggleSection(sectionKey)}>
          <span class="material-icons ${collapsed ? 'collapsed' : ''}">expand_more</span>
          <span class="gear-group-name">${groupName}</span>
          <span class="gear-group-count">${packedCount}/${items.length}</span>
        </div>
        ${collapsed
          ? nothing
          : html`
              <md-list>
                ${repeat(
                  PlanDetail.sortedByPacked(items),
                  item => item.id,
                  item => this.renderPackItem(item, list)
                )}
              </md-list>
            `}
      </div>
    `
  }

  private renderGearSection(
    label: string,
    items: PlanPackItem[],
    list: 'team' | 'individual',
    sectionClass: string
  ): unknown {
    if (items.length === 0) return nothing
    const collapsed = this.collapsedSections.has(list)
    const packedCount = items.filter(i => i.packed).length
    const groups = groupPackItems(items)

    return html`
      <div class="${sectionClass}">
        <div class="gear-section-header" @click=${() => this.toggleSection(list)}>
          <span class="material-icons ${collapsed ? 'collapsed' : ''}">expand_more</span>
          <span class="subsection-label">${label}</span>
          <span class="gear-section-count">${packedCount}/${items.length}</span>
        </div>
        ${collapsed ? nothing : groups.map(g => this.renderGearGroup(g.name, g.items, list))}
      </div>
    `
  }

  private renderPackList(): unknown {
    const p = this.plan!
    if (p.teamGear.length === 0 && p.individualGear.length === 0) return nothing

    return html`
      <div class="section">
        ${this.renderGearSection('Team Gear', p.teamGear, 'team', 'team-gear')}
        ${this.renderGearSection(
          'Individual Gear',
          p.individualGear,
          'individual',
          'individual-gear'
        )}
      </div>
    `
  }

  private renderConditions(): unknown {
    const w = this.weather

    return html` ${w ? this.renderWeatherData(w) : nothing} ${this.renderAvalancheSection()} `
  }

  private renderAvalancheSection(): unknown {
    if (this.avalancheLoading) {
      return html`<div class="section">
        <h2 class="section-title">Avalanche Forecast</h2>
        <div class="weather-loading">Loading avalanche forecast...</div>
      </div>`
    }

    const avy = this.avalanche?.data
    if (!avy) {
      return html`<div class="section">
        <h2 class="section-title">Avalanche Forecast</h2>
        <div class="empty-tab">No avalanche forecast available for this location</div>
      </div>`
    }

    // Ensure the link includes full-precision coordinates from the plan
    const coords = this.resolvePlanCoordinates()
    let link = avy.source.link
    if (link && coords) {
      try {
        const url = new URL(link)
        url.searchParams.set('lat', String(coords[0]))
        url.searchParams.set('lng', String(coords[1]))
        link = url.toString()
      } catch {
        const sep = link.includes('?') ? '&' : '?'
        link = `${link}${sep}lat=${coords[0]}&lng=${coords[1]}`
      }
    }

    return html`
      <div class="section">
        <div class="avy-title-row">
          <h2 class="section-title">Avalanche Forecast</h2>
          ${link
            ? html`<a class="avy-source-tag" href=${link} target="_blank" rel="noopener">
                ${avy.source.centerId}
              </a>`
            : html`<span class="avy-source-tag">${avy.source.centerId}</span>`}
        </div>

        <!-- Period carousel -->
        ${avy.periods.length > 0 ? this.renderAvyCarousel(avy) : nothing}
      </div>
    `
  }

  private renderAvyCarousel(avy: AvalancheForecast): unknown {
    const dates = this.avyDateRange.length > 0 ? this.avyDateRange : avy.periods.map(p => p.date)
    const idx = Math.min(Math.max(0, this.avyPeriodIndex), dates.length - 1)
    const currentDate = dates[idx]
    const period = avy.periods.find(p => p.date === currentDate)
    const hasPrev = idx > 0
    const hasNext = idx < dates.length - 1

    const dateLabel = new Date(currentDate + 'T12:00:00').toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })

    const navigate = (newIdx: number) => {
      this.avyPeriodIndex = newIdx
      const targetDate = dates[newIdx]
      if (targetDate && !this.avyFetchedDates.has(targetDate)) {
        void this.loadAvyDay(targetDate)
      }
      void this.prefetchAvyDay(targetDate)
    }

    return html`
      <div class="avy-carousel-nav">
        <button class="avy-carousel-btn" ?disabled=${!hasPrev} @click=${() => navigate(idx - 1)}>
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path d="M13 4l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
        <span class="avy-carousel-date">${dateLabel}</span>
        <button class="avy-carousel-btn" ?disabled=${!hasNext} @click=${() => navigate(idx + 1)}>
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path d="M7 4l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" />
          </svg>
        </button>
      </div>
      ${this.avyDayLoading && !period
        ? html`<div class="loading">
            <div class="spinner"></div>
            Loading forecast...
          </div>`
        : period
          ? this.renderAvyPeriod(period)
          : html`<div class="avy-period">
              <div class="avy-period-centered">
                <div class="avy-no-data-wrap">
                  <svg class="avy-no-data-icon" width="160" height="140" viewBox="0 0 160 140">
                    <polygon
                      points="80,6 8,134 152,134"
                      fill="#e2e8f0"
                      stroke="#94a3b8"
                      stroke-width="1.5"
                    />
                    <text
                      x="80"
                      y="90"
                      text-anchor="middle"
                      fill="#94a3b8"
                      font-size="14"
                      font-weight="500"
                    >
                      No forecast
                    </text>
                    <text x="80" y="108" text-anchor="middle" fill="#94a3b8" font-size="12">
                      available
                    </text>
                  </svg>
                </div>
              </div>
            </div>`}
    `
  }

  private renderAvyPeriod(period: ForecastPeriod): unknown {
    // No forecast available for this date
    if (period.overallDanger === -1) {
      return html`
        <div class="avy-period">
          <div class="avy-period-centered">
            <div class="avy-no-data-wrap">
              <svg class="avy-no-data-icon" width="160" height="140" viewBox="0 0 160 140">
                <polygon
                  points="80,6 8,134 152,134"
                  fill="#e2e8f0"
                  stroke="#94a3b8"
                  stroke-width="1.5"
                />
                <text
                  x="80"
                  y="90"
                  text-anchor="middle"
                  fill="#94a3b8"
                  font-size="14"
                  font-weight="500"
                >
                  No forecast
                </text>
                <text x="80" y="108" text-anchor="middle" fill="#94a3b8" font-size="12">
                  available
                </text>
              </svg>
            </div>
          </div>
        </div>
      `
    }

    const dangers =
      period.dangerByElevation.length > 0
        ? period.dangerByElevation
        : [
            { band: 'above-treeline' as const, level: period.overallDanger },
            { band: 'near-treeline' as const, level: period.overallDanger },
            { band: 'below-treeline' as const, level: period.overallDanger },
          ]

    return html`
      <div class="avy-period">
        <!-- Danger triangle + text -->
        <div class="avy-period-header">
          ${this.renderDangerTriangle(dangers)}
          <div class="avy-bottom-line">
            ${period.travelAdvice
              ? html`<div style="margin-bottom:4px">
                  <span class="avy-detail-label">Travel Advice:</span> ${period.travelAdvice}
                </div>`
              : nothing}
            ${period.summary ? html`<div>${period.summary}</div>` : nothing}
          </div>
        </div>

        <!-- Problems -->
        ${period.problems.length > 0
          ? html`<div class="avy-problems">
              ${period.problems.map(p => this.renderAvyProblemRow(p))}
            </div>`
          : nothing}
      </div>
    `
  }

  /** Render the tiered danger triangle with embedded NAPADS icons */
  private renderDangerTriangle(dangers: ElevationDanger[]): unknown {
    const tiers: { band: ElevationBand; label: string }[] = [
      { band: 'above-treeline', label: 'Above Treeline' },
      { band: 'near-treeline', label: 'Near Treeline' },
      { band: 'below-treeline', label: 'Below Treeline' },
    ]
    const resolved = tiers.map(t => {
      const d = dangers.find(x => x.band === t.band)
      return { ...t, level: d?.level ?? 0 }
    })

    const alpColor = dangerColor(resolved[0].level)
    const tlnColor = dangerColor(resolved[1].level)
    const btlColor = dangerColor(resolved[2].level)

    // Larger triangle: 160px wide, 140px tall
    // Tier boundaries at y=6 (top), y=50, y=94, y=134 (bottom)
    return html`
      <div class="avy-danger-triangle-wrap">
        <svg class="avy-danger-triangle" width="160" height="140" viewBox="0 0 160 140">
          <polygon points="80,6 56,50 104,50" fill="${alpColor}" stroke="#333" stroke-width="1" />
          <polygon
            points="56,50 32,94 128,94 104,50"
            fill="${tlnColor}"
            stroke="#333"
            stroke-width="1"
          />
          <polygon
            points="32,94 8,134 152,134 128,94"
            fill="${btlColor}"
            stroke="#333"
            stroke-width="1"
          />
        </svg>
        ${resolved.map(
          (d, i) => html`
            <div
              class="avy-tri-icon ${['avy-tri-icon-alp', 'avy-tri-icon-tln', 'avy-tri-icon-btl'][
                i
              ]}"
              tabindex="0"
            >
              ${this.renderDangerDiamond(d.level)}
              <span class="avy-tri-tooltip">${dangerLabel(d.level)} — ${d.label}</span>
            </div>
          `
        )}
      </div>
    `
  }

  /** Map danger level to official NAPADS icon filename */
  private dangerIconSrc(level: DangerLevel): string | null {
    const map: Partial<Record<DangerLevel, string>> = {
      1: 'low.png',
      2: 'moderate.png',
      3: 'considerable.png',
      4: 'high.png',
      5: 'extreme.png',
    }
    return map[level] ?? null
  }

  /** Render a danger level icon using official NAPADS imagery */
  private renderDangerDiamond(level: DangerLevel): unknown {
    const src = this.dangerIconSrc(level)
    if (!src) return nothing
    return html`<img
      src="/icons/avy-danger/${src}"
      alt="${dangerLabel(level)}"
      width="32"
      height="32"
      style="flex-shrink:0;object-fit:contain"
    />`
  }

  /**
   * Render a vertical scale with a marker at the given value.
   * Matches the CAIC/avalanche.org style: vertical line with tick labels,
   * filled marker at the active level.
   */
  private renderVerticalScale(_title: string, levels: string[], active: string): unknown {
    const h = 136
    const topPad = 8
    const botPad = 8
    const lineX = 14
    const trackH = h - topPad - botPad
    const step = trackH / (levels.length - 1)

    // Match active value — try exact match first, then substring fallback
    const norm = active.toLowerCase().trim()
    let activeIdx = levels.findIndex(l => l.toLowerCase() === norm)
    if (activeIdx === -1) {
      activeIdx = levels.findIndex(l => norm.includes(l.toLowerCase()))
    }
    if (activeIdx === -1) {
      activeIdx = levels.findIndex(l => l.toLowerCase().includes(norm))
    }

    const ticks = levels.map((label, i) => {
      const y = topPad + i * step
      const isActive = i === activeIdx
      return svg`
        <line x1="${lineX - 4}" y1="${y}" x2="${lineX + 4}" y2="${y}"
          stroke="currentColor" stroke-width="${isActive ? 2 : 1}" opacity="${isActive ? 1 : 0.4}" />
        <text x="${lineX + 12}" y="${y}" dominant-baseline="central"
          font-size="${isActive ? 10 : 8}" font-weight="${isActive ? 700 : 400}"
          fill="currentColor" opacity="${isActive ? 1 : 0.5}">${label}</text>
        ${
          isActive
            ? svg`<rect x="${lineX - 6}" y="${y - 6}" width="12" height="12" rx="2" fill="currentColor" />`
            : nothing
        }
      `
    })

    return svg`
      <line
        x1="${lineX}"
        y1="${topPad}"
        x2="${lineX}"
        y2="${topPad + trackH}"
        stroke="currentColor"
        stroke-width="1"
        opacity="0.3"
      />
      ${ticks}
    `
  }

  private renderLikelihoodScale(value: string): unknown {
    const levels = ['Certain', 'Very Likely', 'Likely', 'Possible', 'Unlikely']
    return this.renderVerticalScale('Likelihood', levels, value)
  }

  private renderSizeScale(value: string): unknown {
    // D-scale ticks: top (largest) to bottom (smallest)
    const ticks = [
      { label: 'D5 - Historic', n: 5 },
      { label: 'D4 - Very Lg', n: 4 },
      { label: 'D3 - Large', n: 3 },
      { label: 'D2 - Small', n: 2 },
      { label: 'D1 - Small', n: 1 },
    ]

    // Parse range — values like "1.5-2", "D2", "3", "1-2"
    const nums =
      value
        .replace(/[Dd]/g, '')
        .match(/\d+\.?\d*/g)
        ?.map(Number) ?? []
    const lo = nums.length > 0 ? Math.min(...nums) : 0
    const hi = nums.length > 0 ? Math.max(...nums) : 0

    const h = 136
    const topPad = 8
    const botPad = 8
    const lineX = 14
    const trackH = h - topPad - botPad
    const step = trackH / (ticks.length - 1)

    // Interpolate a numeric value (1–5) to a y position
    // ticks[0].n = 5 at y = topPad, ticks[4].n = 1 at y = topPad + trackH
    const valToY = (n: number): number => topPad + ((5 - n) / 4) * trackH

    const tickEls = ticks.map(({ label, n }, i) => {
      const y = topPad + i * step
      const inRange = lo > 0 && n >= Math.floor(lo) && n <= Math.ceil(hi)
      return svg`
        <line x1="${lineX - 4}" y1="${y}" x2="${lineX + 4}" y2="${y}"
          stroke="currentColor" stroke-width="${inRange ? 2 : 1}" opacity="${inRange ? 1 : 0.4}" />
        <text x="${lineX + 12}" y="${y}" dominant-baseline="central"
          font-size="${inRange ? 10 : 8}" font-weight="${inRange ? 700 : 400}"
          fill="currentColor" opacity="${inRange ? 1 : 0.5}">${label}</text>
      `
    })

    // Filled rectangle for the range
    const boxW = 12
    const markers =
      lo > 0
        ? svg`<rect
          x="${lineX - boxW / 2}" y="${valToY(hi)}"
          width="${boxW}" height="${Math.max(valToY(lo) - valToY(hi), 2)}"
          rx="2" fill="currentColor"
        />`
        : nothing

    return svg`
      <line
        x1="${lineX}"
        y1="${topPad}"
        x2="${lineX}"
        y2="${topPad + trackH}"
        stroke="currentColor"
        stroke-width="1"
        opacity="0.3"
      />
      ${markers} ${tickEls}
    `
  }

  /** Render a problem row: problem | aspect | likelihood | size — all titled columns */
  private renderAvyProblemRow(p: AvalancheProblem): unknown {
    const hasRose = p.locations.length > 0 || p.aspects.length > 0 || p.elevations.length > 0
    return html`
      <div class="avy-problem-row">
        <div class="avy-problem-col">
          <div class="avy-problem-col-title">Problem:<br />${problemLabel(p.type)}</div>
          <div class="avy-problem-icon">${this.renderProblemIcon(p.type)}</div>
        </div>
        ${hasRose
          ? html`<div class="avy-problem-col">
              <div class="avy-problem-col-title">Aspect</div>
              ${this.renderAspectRose(p.aspects, p.elevations, p.locations)}
            </div>`
          : nothing}
        ${p.likelihood
          ? html`<div class="avy-problem-col">
              <div class="avy-problem-col-title">Likelihood</div>
              <svg class="avy-scale-svg" width="100" height="136" viewBox="0 0 100 136">
                ${this.renderLikelihoodScale(p.likelihood)}
              </svg>
            </div>`
          : nothing}
        ${p.expectedSize
          ? html`<div class="avy-problem-col">
              <div class="avy-problem-col-title">Size</div>
              <svg class="avy-scale-svg" width="100" height="136" viewBox="0 0 100 136">
                ${this.renderSizeScale(p.expectedSize)}
              </svg>
            </div>`
          : nothing}
      </div>
    `
  }

  /**
   * Render the aspect/elevation rose.
   * 3 concentric octagonal rings: inner=above-treeline, mid=near-treeline, outer=below-treeline.
   * Filled wedges for affected aspect+elevation combos.
   */
  private renderAspectRose(
    aspects: Aspect[],
    elevations: ElevationBand[],
    locations?: Array<{ aspect: Aspect; elevation: ElevationBand }>
  ): unknown {
    const size = 160
    const cx = size / 2
    const cy = size / 2

    // Build a set of precise "aspect:elevation" keys for O(1) lookup
    const locationSet = new Set<string>()
    if (locations && locations.length > 0) {
      for (const loc of locations) {
        locationSet.add(`${loc.aspect}:${loc.elevation}`)
      }
    }
    const hasPreciseLocations = locationSet.size > 0

    // Fallback to cross-product when no precise locations
    const aspectSet = new Set(aspects)
    const elevSet = new Set(elevations)

    // Ring radii — above-treeline starts at center (no hole)
    const rings: { band: ElevationBand; r1: number; r2: number }[] = [
      { band: 'above-treeline', r1: 0, r2: 21 },
      { band: 'near-treeline', r1: 21, r2: 42 },
      { band: 'below-treeline', r1: 42, r2: 60 },
    ]

    const directions: Aspect[] = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
    // Vertex angles for the octagon edges (between adjacent directions)
    const edgeAngles = directions.map((_, i) => ((i * 45 - 90 + 22.5) * Math.PI) / 180)

    const wedges: unknown[] = []
    for (const ring of rings) {
      for (let i = 0; i < 8; i++) {
        const dir = directions[i]
        const filled = hasPreciseLocations
          ? locationSet.has(`${dir}:${ring.band}`)
          : elevSet.has(ring.band) && aspectSet.has(dir)
        // Edge angles bounding this segment
        const eL = edgeAngles[(i + 7) % 8] // left edge
        const eR = edgeAngles[i] // right edge

        const path =
          ring.r1 === 0
            ? this.octWedgeCenter(cx, cy, ring.r2, eL, eR)
            : this.octWedgePath(cx, cy, ring.r1, ring.r2, eL, eR)
        wedges.push(
          svg`<path d="${path}" fill="${filled ? '#1e3a5f' : '#f1f5f9'}" stroke="#94a3b8" stroke-width="0.5" />`
        )
      }
    }

    // Direction labels
    const labelRadius = 72
    const labels = directions.map((dir, i) => {
      const angle = ((i * 45 - 90) * Math.PI) / 180
      const lx = cx + labelRadius * Math.cos(angle)
      const ly = cy + labelRadius * Math.sin(angle)
      return svg`<text x="${lx}" y="${ly}" text-anchor="middle" dominant-baseline="central"
        font-size="10" fill="currentColor" font-weight="700">${dir}</text>`
    })

    return html`<svg
      class="avy-rose-svg"
      width="${size}"
      height="${size}"
      viewBox="0 0 ${size} ${size}"
    >
      ${wedges} ${labels}
    </svg>`
  }

  /** Triangle wedge from center to two octagon vertices at radius r */
  private octWedgeCenter(cx: number, cy: number, r: number, aL: number, aR: number): string {
    const x1 = cx + r * Math.cos(aL)
    const y1 = cy + r * Math.sin(aL)
    const x2 = cx + r * Math.cos(aR)
    const y2 = cy + r * Math.sin(aR)
    return `M${cx},${cy} L${x1},${y1} L${x2},${y2} Z`
  }

  /** Trapezoid wedge between inner and outer octagon rings */
  private octWedgePath(
    cx: number,
    cy: number,
    r1: number,
    r2: number,
    aL: number,
    aR: number
  ): string {
    const xoL = cx + r2 * Math.cos(aL)
    const yoL = cy + r2 * Math.sin(aL)
    const xoR = cx + r2 * Math.cos(aR)
    const yoR = cy + r2 * Math.sin(aR)
    const xiR = cx + r1 * Math.cos(aR)
    const yiR = cy + r1 * Math.sin(aR)
    const xiL = cx + r1 * Math.cos(aL)
    const yiL = cy + r1 * Math.sin(aL)
    return `M${xoL},${yoL} L${xoR},${yoR} L${xiR},${yiR} L${xiL},${yiL} Z`
  }

  /** Map problem type to official CAIC icon filename */
  private problemIconSrc(type: AvalancheProblemType): string {
    const map: Record<AvalancheProblemType, string> = {
      'storm-slab': 'Storm.Slabs_.png',
      'wind-slab': 'Wind.Slabs_.png',
      'persistent-slab': 'Persistent.Slabs_.png',
      'deep-slab': 'Deep.Persistent.Slabs_.png',
      'wet-loose': 'Loose.Wet_.png',
      'wet-slab': 'Wet.Slabs_.png',
      'dry-loose': 'Loose.Dry_.png',
      cornice: 'Cornice.png',
      glide: 'Glide.png',
    }
    return `/icons/avy-problems/${map[type]}`
  }

  /** Render the official CAIC problem type icon */
  private renderProblemIcon(type: AvalancheProblemType): unknown {
    return html`<img src="${this.problemIconSrc(type)}" alt="${problemLabel(type)}" />`
  }

  private renderWeatherData(w: WeatherResult): unknown {
    const { current } = w.data

    // Resolve which data source to show based on selected chip
    const activeChipId = [...this.activeWeatherChips][0] ?? 'area'
    const isArea = activeChipId === 'area'
    const activePoint = isArea ? null : this.elevationWeatherPoints.find(p => p.id === activeChipId)
    const activeData = activePoint?.data ?? w.data
    const daily = activeData.daily
    const allHourly = activeData.hourly

    // Trim to next 24 hours for the strip
    const now = Date.now()
    const in24h = now + 24 * 60 * 60 * 1000
    const hourly = allHourly.filter(h => {
      const t = new Date(h.time).getTime()
      return t >= now && t <= in24h
    })

    // Determine plan date range for highlighting
    const dateRange = this.resolvePlanDateRange()
    const planDates = dateRange
      ? new Set(
          (() => {
            const dates: string[] = []
            const d = new Date(dateRange.startDate + 'T12:00:00Z')
            const end = new Date(dateRange.endDate + 'T12:00:00Z')
            while (d <= end) {
              dates.push(d.toISOString().slice(0, 10))
              d.setUTCDate(d.getUTCDate() + 1)
            }
            return dates
          })()
        )
      : new Set<string>()

    const timeAgo = this.formatTimeAgo(w.fetchedAt)

    return html`
      <div class="section">
        <!-- Current conditions card -->
        <div class="weather-current">
          <div class="weather-current-main">
            <span class="material-icons weather-current-icon"
              >${PlanDetail.skyConditionIcon(current.skyCondition)}</span
            >
            <div>
              <div class="weather-current-temp">
                ${this.tempVal(current.temperature)}${this.tempUnit}
              </div>
              <div class="weather-current-feels">
                Feels like ${this.tempVal(current.apparentTemperature)}${this.tempUnit}
              </div>
            </div>
          </div>
          <div class="weather-current-time">
            ${new Date(current.fetchedAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </div>
          <div>
            <div class="weather-current-condition">
              ${PlanDetail.skyConditionLabel(current.skyCondition)}
            </div>
            <div class="weather-current-details">
              <span class="weather-detail">
                <span class="material-icons">air</span>
                ${this.windVal(current.windSpeed)}
                ${this.windUnit}${current.windGusts > current.windSpeed + 5
                  ? html`, gusts ${this.windVal(current.windGusts)}`
                  : nothing}
              </span>
              <span class="weather-detail">
                <span class="material-icons">water_drop</span>
                ${current.humidity}%
              </span>
              <span class="weather-detail">
                <span class="material-icons">cloud</span>
                ${current.cloudCover}%
              </span>
            </div>
          </div>
        </div>
        <!-- Weather source chips -->
        ${this.elevationWeatherPoints.length > 0
          ? html`
              <div class="weather-chips">
                <button
                  class="weather-chip ${this.activeWeatherChips.has('area') ? 'active' : ''}"
                  @click=${() => this.toggleWeatherChip('area')}
                >
                  Area
                </button>
                ${this.elevationWeatherPoints.map(
                  p => html`
                    <button
                      class="weather-chip ${this.activeWeatherChips.has(p.id) ? 'active' : ''}"
                      @click=${() => this.toggleWeatherChip(p.id)}
                    >
                      ${p.label}
                      <span class="weather-chip-elev">${p.elevationFt.toLocaleString()} ft</span>
                    </button>
                  `
                )}
              </div>
            `
          : nothing}
        <!-- Hourly forecast strip -->
        ${hourly.length > 0
          ? html`
              <div class="weather-hourly-header">
                <span class="weather-hourly-label">Next 24 Hours</span>
                <div class="weather-hourly-controls">
                  <div class="weather-granularity-toggle">
                    ${([1, 3, 6] as const).map(
                      g => html`
                        <button
                          class="granularity-btn ${this.hourlyGranularity === g ? 'active' : ''}"
                          @click=${() => {
                            this.hourlyGranularity = g
                          }}
                        >
                          ${g}h
                        </button>
                      `
                    )}
                  </div>
                  <span class="weather-updated">
                    Updated ${timeAgo}${w.fresh ? '' : ' (refreshing...)'}
                  </span>
                </div>
              </div>
              <div class="weather-hourly">
                ${this.aggregateHourly(hourly, this.hourlyGranularity).map(
                  h => html`
                    <div class="weather-hour">
                      <span class="weather-hour-time">${h.label}</span>
                      <span class="material-icons weather-hour-icon"
                        >${PlanDetail.skyConditionIcon(h.sky)}</span
                      >
                      <span class="weather-hour-temp">${this.tempVal(h.temp)}°</span>
                      <div class="weather-hour-precip-slot">
                        <span class="weather-hour-precip-icon material-icons"
                          >${h.snow > h.precip ? 'ac_unit' : 'water_drop'}</span
                        >
                        <span class="weather-hour-accum"
                          >${this.precipVal(h.snow > h.precip ? h.snow : h.precip)}${this
                            .precipUnit}</span
                        >
                      </div>
                      ${capeToLightningRisk(h.cape) !== 'low'
                        ? html`<span
                            class="material-icons weather-hour-lightning"
                            style="color: ${PlanDetail.lightningRiskColor(
                              capeToLightningRisk(h.cape)
                            )}"
                            >electric_bolt</span
                          >`
                        : nothing}
                    </div>
                  `
                )}
              </div>
            `
          : nothing}

        <!-- Daily forecast -->
        ${daily.length > 0
          ? html`
              <div class="weather-daily">
                ${daily.map(d => {
                  const expanded = this.expandedWeatherDays.has(d.date)
                  const dayHours = allHourly.filter(h => h.time.startsWith(d.date))
                  const peakCape = Math.max(0, ...dayHours.map(h => h.cape))
                  const peakRisk = capeToLightningRisk(peakCape)
                  return html`
                    <div
                      class="weather-day ${planDates.has(d.date) ? 'plan-day' : ''} ${expanded
                        ? 'weather-day-expanded'
                        : ''}"
                    >
                      <div
                        class="weather-day-summary"
                        @click=${() => this.toggleWeatherDay(d.date)}
                      >
                        <span class="material-icons weather-day-chevron"
                          >${expanded ? 'expand_more' : 'chevron_right'}</span
                        >
                        <div class="weather-day-row1">
                          <span class="weather-day-name"
                            >${new Date(d.date + 'T12:00').toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}</span
                          >
                          <span class="material-icons weather-day-icon"
                            >${PlanDetail.skyConditionIcon(d.skyCondition)}</span
                          >
                          <span class="weather-day-condition"
                            >${PlanDetail.skyConditionLabel(d.skyCondition)}</span
                          >
                          <span class="weather-day-precip">
                            ${d.precipProbabilityMax > 0 ||
                            (d.precipProbabilityMax === -1 &&
                              (d.precipSum > 0 || d.snowfallSum > 0))
                              ? html`<span class="material-icons"
                                    >${d.snowfallSum > d.precipSum ? 'ac_unit' : 'water_drop'}</span
                                  >
                                  ${d.precipProbabilityMax >= 0
                                    ? html`${d.precipProbabilityMax}%`
                                    : nothing}${(() => {
                                    const amount =
                                      d.snowfallSum > d.precipSum ? d.snowfallSum : d.precipSum
                                    if (amount <= 0) return nothing
                                    const sep = d.precipProbabilityMax >= 0 ? ' · ' : ''
                                    return html`${sep}${this.precipVal(amount)}${this.precipUnit}`
                                  })()}`
                              : nothing}
                          </span>
                        </div>
                        <div class="weather-day-row2">
                          ${peakRisk !== 'low'
                            ? html`<span
                                class="material-icons weather-day-lightning"
                                style="color: ${PlanDetail.lightningRiskColor(peakRisk)}"
                                >electric_bolt</span
                              >`
                            : nothing}
                          <span class="weather-day-wind">
                            <span class="material-icons">air</span>
                            ${d.windGustsMax > d.windSpeedMax + 5
                              ? html`${this.windVal(d.windSpeedMax)}-${this.windVal(d.windGustsMax)}
                                ${this.windUnit}`
                              : html`${this.windVal(d.windSpeedMax)} ${this.windUnit}`}
                          </span>
                        </div>
                        <span class="weather-day-temps">
                          <span class="weather-day-high">${this.tempVal(d.temperatureHigh)}°</span>
                          <span class="weather-day-low">${this.tempVal(d.temperatureLow)}°</span>
                        </span>
                      </div>
                      ${expanded ? this.renderWeatherDayDetail(d, dayHours) : nothing}
                    </div>
                  `
                })}
              </div>
              ${this.resolvePlanDateRange()
                ? html`<button
                    class="weather-show-more"
                    @click=${() => {
                      this.weatherExtraPadding += 3
                      void this.loadWeather(true)
                    }}
                  >
                    Show more days
                  </button>`
                : nothing}
            `
          : nothing}
      </div>
    `
  }

  private formatTimeAgo(isoString: string): string {
    const diff = Date.now() - new Date(isoString).getTime()
    const minutes = Math.floor(diff / 60000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  private hasPackList(): boolean {
    const p = this.plan!
    return p.teamGear.length > 0 || p.individualGear.length > 0
  }

  private hasConditions(): boolean {
    return true // Always show — avy forecast section is always visible
  }

  private renderTabContent(): unknown {
    switch (this.tab) {
      case 'overview':
        return html`${this.renderOverview()}${this.renderEmergencyContacts()}${this.renderEmergencyServices()}${this.renderMembers()}`
      case 'pack-list':
        return this.hasPackList()
          ? this.renderPackList()
          : html`<div class="empty-tab">No gear added yet</div>`
      case 'conditions':
        if (this.weatherLoading) return html`<div class="weather-loading">Loading weather...</div>`
        if (!this.weather && !this.resolvePlanCoordinates())
          return html`<div class="weather-no-location">
            <span class="material-icons">location_off</span>
            <div>Add a location to this plan to see weather conditions</div>
          </div>`
        return this.hasConditions()
          ? this.renderConditions()
          : html`<div class="empty-tab">No weather data available</div>`
      case 'field-log':
        return html`<field-log-tab
          .planId=${this.plan!.id}
          .plan=${this.plan}
          .currentUserId=${authService.getState().userId ?? ''}
          .currentUserRole=${this.currentUserRole ?? 'viewer'}
          .members=${this.members}
          .lastSyncTime=${this.lastSyncTime}
        ></field-log-tab>`
    }
  }

  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    if (!this.planId) return
    this.syncing = true
    try {
      await syncChildrenOnNavigate('plan', this.planId)
      await this.loadPlan()
      this.lastSyncTime = Date.now()
    } finally {
      this.syncing = false
    }
  }

  override render(): unknown {
    if (!this.plan) {
      return html`<div class="container">
        <div class="content-wrapper">
          <div class="content"><p>Loading plan...</p></div>
        </div>
      </div>`
    }

    const p = this.plan
    return html`
      <div class="container">
        <div class="header">
          <button class="back-button" @click=${() => window.history.back()}>
            <span class="material-icons">arrow_back</span>
          </button>
          <h1 class="header-title">${p.name}</h1>
        </div>
        ${this.syncing
          ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
          : nothing}
        <div
          class="content-scroll"
          @touchstart=${this.handleTouchStart}
          @touchmove=${this.handleTouchMove}
          @touchend=${this.handleTouchEnd}
        >
          <div class="content-wrapper">
            <div class="content">
              <div class="plan-header">
                <h1 class="plan-name">${p.name}</h1>
                <div class="plan-actions">
                  <md-icon-button
                    @click=${() => router.navigate(`/plans/${p.id}/download`)}
                    aria-label="Download for offline"
                  >
                    <span class="material-icons">download_for_offline</span>
                  </md-icon-button>
                  <md-icon-button
                    @click=${() => void this.handlePromoteToRoute()}
                    aria-label="Save as route"
                  >
                    <span class="material-icons">route</span>
                  </md-icon-button>
                  <md-icon-button
                    @click=${() => router.navigate(`/plans/${p.id}/edit`)}
                    aria-label="Edit plan"
                  >
                    <span class="material-icons">edit</span>
                  </md-icon-button>
                  <md-icon-button @click=${() => this.handleDelete()} aria-label="Delete plan">
                    <span class="material-icons">delete</span>
                  </md-icon-button>
                </div>
              </div>

              <div class="tab-bar">
                <button
                  class="tab ${this.tab === 'overview' ? 'active' : ''}"
                  @click=${() => router.navigate(`/plans/${p.id}`, { replace: true })}
                >
                  Overview
                </button>
                <button
                  class="tab ${this.tab === 'conditions' ? 'active' : ''}"
                  @click=${() => router.navigate(`/plans/${p.id}/conditions`, { replace: true })}
                >
                  Conditions
                </button>
                <button
                  class="tab ${this.tab === 'pack-list' ? 'active' : ''}"
                  @click=${() => router.navigate(`/plans/${p.id}/pack-list`, { replace: true })}
                >
                  Gear
                </button>
                <button
                  class="tab ${this.tab === 'field-log' ? 'active' : ''}"
                  @click=${() => router.navigate(`/plans/${p.id}/field-log`, { replace: true })}
                >
                  Field Log
                </button>
                ${this.tab === 'conditions'
                  ? html`<md-icon-button
                      class="tab-refresh${this.weatherLoading || this.avalancheLoading
                        ? ' spinning'
                        : ''}"
                      @click=${() => this.refreshConditions()}
                      ?disabled=${this.weatherLoading || this.avalancheLoading}
                      aria-label="Refresh conditions"
                    >
                      <span class="material-icons">refresh</span>
                    </md-icon-button>`
                  : nothing}
              </div>

              ${this.renderTabContent()}
            </div>
          </div>
          <confirm-dialog></confirm-dialog>
          <route-promote-dialog></route-promote-dialog>
        </div>
      </div>
    `
  }

  private async handlePromoteToRoute(): Promise<void> {
    if (!this.plan) return
    const dialog = this.shadowRoot?.querySelector('route-promote-dialog') as
      | RoutePromoteDialog
      | undefined
    if (!dialog) return
    dialog.planId = this.plan.id
    dialog.planName = this.plan.name
    dialog.createdBy = this.plan.createdBy
    const routeId = await dialog.show()
    if (routeId) {
      router.navigate(`/routes/${routeId}`)
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'plan-detail': PlanDetail
  }
}
